import { useState, useEffect, useMemo, useRef } from "react";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend } from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

// ── Fragment-comment helpers: map a DOM selection to char offsets in a container, and highlight ranges ──
function charOffsetOf(container, node, offset) {
  let count = 0, res = 0, done = false;
  (function walk(n) {
    if (done) return;
    if (n === node) { res = count + offset; done = true; return; }
    if (n.nodeType === 3) { count += n.nodeValue.length; }
    else { for (let c = n.firstChild; c; c = c.nextSibling) { walk(c); if (done) return; } }
  })(container);
  return res;
}
function highlightFragRange(container, start, end) {
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);
  let pos = 0; const spans = []; let node;
  while ((node = walker.nextNode())) {
    const len = node.nodeValue.length, ns = pos, ne = pos + len;
    if (ne > start && ns < end) spans.push({ node, s: Math.max(start, ns) - ns, e: Math.min(end, ne) - ns });
    pos = ne; if (pos >= end) break;
  }
  for (const sp of spans) {
    try {
      const r = document.createRange();
      r.setStart(sp.node, sp.s); r.setEnd(sp.node, sp.e);
      const mark = document.createElement("mark");
      mark.setAttribute("data-frag", "1");
      mark.style.background = "rgba(99,102,241,0.28)";
      mark.style.borderRadius = "2px";
      r.surroundContents(mark);
    } catch (e) {}
  }
}

// ─── Color palette (dark cyber-style with light mode fallback) ─────
const PAL = {
  brand:  "#6366f1",
  brand2: "#a855f7",
  pos:    "#10b981",
  neg:    "#ef4444",
  warn:   "#f59e0b",
  info:   "#3b82f6",
};
// Convert hex to rgba string (for chart.js fill backgrounds)
function hexA(hex, a) {
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
}
// Chart.js options factory in report theme (dark/light aware)
function chartOpts(t, dark, { yLabels = true, dualY = false, labelFmt = null, yPct = false } = {}) {
  const grid = dark ? "rgba(255,255,255,.05)" : "rgba(0,0,0,.05)";
  const baseY = { ticks: { display: yLabels, color: t.fg2, font: { size: 10, family: FONT_MONO }, callback: yPct ? (v) => v + "%" : undefined }, grid: { color: grid }, border: { display: false }, beginAtZero: true };
  return {
    responsive: true, maintainAspectRatio: false,
    interaction: { intersect: false, mode: "index" },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: dark ? "rgba(20,20,32,0.96)" : "rgba(255,255,255,0.98)",
        titleColor: t.fg0, bodyColor: t.fg0, borderColor: t.line, borderWidth: 1,
        padding: 10, cornerRadius: 8, bodyFont: { size: 11.5, family: FONT_MONO }, titleFont: { size: 11 },
        callbacks: { label: labelFmt || ((ctx) => `${ctx.dataset.label}: ${Number(ctx.parsed.y).toLocaleString("uk-UA")}`) },
      },
    },
    scales: dualY ? {
      x: { ticks: { color: t.fg2, font: { size: 10, family: FONT_MONO }, maxTicksLimit: 10 }, grid: { display: false }, border: { display: false } },
      y:  { position: "left",  ticks: { color: PAL.brand2, font: { size: 10, family: FONT_MONO } }, grid: { color: grid }, border: { display: false }, beginAtZero: true },
      y1: { position: "right", ticks: { color: PAL.brand,  font: { size: 10, family: FONT_MONO } }, grid: { display: false }, border: { display: false }, beginAtZero: true },
    } : {
      x: { ticks: { color: t.fg2, font: { size: 10, family: FONT_MONO }, maxTicksLimit: 10 }, grid: { display: false }, border: { display: false } },
      y: baseY,
    },
  };
}
const themeFor = (dark) => dark ? {
  bg0: "#0a0a14", bg1: "#11111d", bg2: "#1a1a28", bg3: "#222232",
  fg0: "#f0f0f8", fg1: "#c8c8d6", fg2: "#8a8aa0", fg3: "#5a5a72",
  line: "#2a2a3e",
} : {
  bg0: "#f8f9fb", bg1: "#ffffff", bg2: "#f3f4f6", bg3: "#e5e7eb",
  fg0: "#0f0f1e", fg1: "#3a3a4e", fg2: "#6b6b80", fg3: "#9999ad",
  line: "#e5e5ed",
};
const FONT_MONO = '"JetBrains Mono", "SF Mono", Menlo, monospace';

// ─── Helpers ──────────────────────────────────────────────────────
function fmtDate(d) {
  if (!d) return "";
  const s = String(d).slice(0, 10);
  const [y, m, day] = s.split("-");
  return day && m && y ? `${day}.${m}.${y}` : s;
}
function fmtDateShort(d) {
  if (!d) return "";
  const s = String(d).slice(0, 10);
  const [y, m, day] = s.split("-");
  return day && m ? `${day}.${m}` : s;
}
function deltaPct(curr, prev) {
  if (!prev || prev === 0) return curr > 0 ? 100 : null;
  return Math.round(((curr - prev) / prev) * 100);
}
// % ключів у TOP-N від загальної кількості відстежуваних ключів снапшоту
function pctOf(n, total) { return total > 0 ? Math.round((n / total) * 100) : 0; }
function fmtNum(n) {
  if (n == null) return "—";
  return Number(n).toLocaleString("uk-UA");
}

// ─── Custom SVG components ────────────────────────────────────────
function Sparkline({ data, color, h = 32, w = 100 }) {
  if (!data || data.length < 2) return <div style={{ height: h, width: w }} />;
  const max = Math.max(...data), min = Math.min(...data), span = Math.max(1, max - min);
  const step = w / (data.length - 1);
  const pts = data.map((v, i) => `${(i * step).toFixed(1)},${(h - ((v - min) / span) * h).toFixed(1)}`).join(" ");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: "block" }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={w} cy={(h - ((data.at(-1) - min) / span) * h).toFixed(1)} r="2.5" fill={color} />
    </svg>
  );
}

function LineChart({ data, series, h = 240, t, dark }) {
  const padL = 40, padR = 16, padT = 12, padB = 26;
  const w = 760;
  const innerW = w - padL - padR, innerH = h - padT - padB;
  const [hover, setHover] = useState(null);

  if (!data || !data.length || !series || !series.length) {
    return <div style={{ height: h, color: t.fg2, textAlign: "center", paddingTop: h/2 - 10, fontSize: 12 }}>немає даних</div>;
  }
  const allVals = [];
  series.forEach(s => data.forEach(d => { const v = d[s.key]; if (v != null) allVals.push(v); }));
  const max = Math.max(...allVals, 0), min = Math.min(...allVals, 0);
  const span = Math.max(1, max - min);
  const xStep = data.length > 1 ? innerW / (data.length - 1) : 0;
  const yFor = (v) => padT + innerH - ((v - min) / span) * innerH;
  const tickEvery = Math.max(1, Math.ceil(data.length / 8));

  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const xRel = ((e.clientX - rect.left) / rect.width) * w;
    const idx = Math.round((xRel - padL) / xStep);
    if (idx >= 0 && idx < data.length) setHover(idx);
  };
  const onLeave = () => setHover(null);

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <svg width="100%" viewBox={`0 0 ${w} ${h}`} style={{ display: "block", cursor: "crosshair" }}
        onMouseMove={onMove} onMouseLeave={onLeave}>
        {/* Y grid */}
        {[0, 0.25, 0.5, 0.75, 1].map((g, i) => (
          <line key={i} x1={padL} x2={w-padR} y1={padT + innerH * g} y2={padT + innerH * g}
            stroke={dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"} strokeWidth="1" />
        ))}
        {/* Y labels */}
        {[0, 0.5, 1].map((g, i) => (
          <text key={i} x={padL - 4} y={padT + innerH * (1-g) + 3} textAnchor="end" fontSize="9" fill={t.fg3} fontFamily={FONT_MONO}>
            {Math.round(min + span * g)}
          </text>
        ))}
        {/* Series — fill + line */}
        {series.map((s, si) => {
          const pts = data.map((d, i) => `${(padL + i * xStep).toFixed(1)},${yFor(d[s.key] || 0).toFixed(1)}`).join(" ");
          const fillPts = `${padL},${padT + innerH} ${pts} ${padL + (data.length - 1) * xStep},${padT + innerH}`;
          const fill = s.fill !== false;
          return (
            <g key={si}>
              {fill && <polyline points={fillPts} fill={s.color} fillOpacity="0.08" stroke="none" />}
              <polyline points={pts} fill="none" stroke={s.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              {data.map((d, i) => (
                <circle key={i} cx={padL + i * xStep} cy={yFor(d[s.key] || 0)} r={hover === i ? 4 : 2.5}
                  fill={s.color} opacity={hover === i ? 1 : (i === data.length - 1 ? 1 : 0)}
                  style={{ transition: "r .1s" }} />
              ))}
            </g>
          );
        })}
        {/* Hover vertical line */}
        {hover != null && (
          <line x1={padL + hover * xStep} x2={padL + hover * xStep} y1={padT} y2={padT + innerH}
            stroke={t.fg2} strokeWidth="1" strokeDasharray="3,3" opacity="0.5" />
        )}
        {/* X labels */}
        {data.map((d, i) => {
          if (i % tickEvery !== 0 && i !== data.length - 1) return null;
          return (
            <text key={i} x={padL + i * xStep} y={h - 8} textAnchor="middle" fontSize="9" fill={t.fg3} fontFamily={FONT_MONO}>
              {d.label}
            </text>
          );
        })}
      </svg>
      {/* Tooltip */}
      {hover != null && (() => {
        const d = data[hover];
        const pct = (hover / Math.max(1, data.length - 1)) * 100;
        const onLeft = pct > 60;
        return (
          <div style={{
            position: "absolute",
            top: "8px",
            [onLeft ? "right" : "left"]: `${onLeft ? 100 - pct : pct}%`,
            marginLeft: onLeft ? 0 : 12, marginRight: onLeft ? 12 : 0,
            background: dark ? "rgba(20,20,32,0.96)" : "rgba(255,255,255,0.98)",
            border: `1px solid ${t.line}`, borderRadius: 8,
            padding: "8px 12px", fontSize: 11.5, color: t.fg0,
            boxShadow: dark ? "0 4px 16px rgba(0,0,0,0.4)" : "0 4px 16px rgba(0,0,0,0.1)",
            pointerEvents: "none", minWidth: 130, zIndex: 10,
          }}>
            <div style={{ fontSize: 10.5, color: t.fg2, marginBottom: 4, fontFamily: FONT_MONO }}>{d.label}</div>
            {series.map((s, si) => (
              <div key={si} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginTop: 2 }}>
                <span style={{ display: "flex", gap: 5, alignItems: "center", color: t.fg2 }}>
                  <span style={{ width: 6, height: 6, borderRadius: 3, background: s.color }} />
                  {s.label || s.key}
                </span>
                <span style={{ fontFamily: FONT_MONO, fontWeight: 600, color: t.fg0 }}>{(d[s.key] || 0).toLocaleString("uk-UA")}</span>
              </div>
            ))}
          </div>
        );
      })()}
    </div>
  );
}

function ClientChip({ kind, children, t }) {
  const map = {
    pos:  { c: PAL.pos,   bg: "rgba(16,185,129,0.12)" },
    neg:  { c: PAL.neg,   bg: "rgba(239,68,68,0.12)" },
    warn: { c: PAL.warn,  bg: "rgba(245,158,11,0.14)" },
    info: { c: PAL.info,  bg: "rgba(59,130,246,0.12)" },
  };
  const s = map[kind] || { c: t.fg1, bg: t.bg3 };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "3px 9px", borderRadius: 4,
      background: s.bg, color: s.c,
      fontSize: 11, fontWeight: 600, fontFamily: FONT_MONO, letterSpacing: "0.05em",
    }}>{children}</span>
  );
}

function AINarrator({ headline, body, tone = "info", t }) {
  const c = PAL[tone] || PAL.info;
  return (
    <div style={{
      padding: "14px 16px", borderRadius: 10,
      background: `${c}10`,
      border: `1px solid ${c}40`,
      display: "flex", gap: 12, alignItems: "flex-start",
    }}>
      <div style={{
        width: 28, height: 28, borderRadius: 8,
        background: `${c}30`, display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0, color: c, fontSize: 16,
      }}>✦</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", gap: 6, marginBottom: 4, alignItems: "center" }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: c, letterSpacing: "0.18em" }}>SPALAH AI</span>
          <span style={{ fontSize: 10, color: t.fg3 }}>· розшифровка для клієнта</span>
        </div>
        <div style={{ fontSize: 14, fontWeight: 600, color: t.fg0, lineHeight: 1.45, marginBottom: 4 }}>{headline}</div>
        <div style={{ fontSize: 12.5, color: t.fg1, lineHeight: 1.55 }}>{body}</div>
      </div>
    </div>
  );
}

function ClientKPI({ label, value, suffix, prefix, delta, hint, tone = "neutral", spark, t, dark }) {
  const toneColor = { pos: PAL.pos, neg: PAL.neg, warn: PAL.warn, info: PAL.info, brand: PAL.brand2, neutral: t.fg1 }[tone];
  return (
    <div style={{
      padding: 18, position: "relative", overflow: "hidden",
      display: "flex", flexDirection: "column", gap: 8, minHeight: 142,
      background: t.bg1, border: `1px solid ${t.line}`, borderRadius: 12,
    }}>
      <div style={{ position: "absolute", inset: 0, background: `linear-gradient(160deg, ${toneColor}15 0%, transparent 50%)`, pointerEvents: "none" }} />
      <div style={{ position: "relative", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: t.fg2, textTransform: "uppercase", letterSpacing: "0.12em" }}>{label}</span>
        {delta != null && <ClientChip kind={delta >= 0 ? "pos" : "neg"} t={t}>{delta >= 0 ? "▲" : "▼"} {Math.abs(delta)}%</ClientChip>}
      </div>
      <div style={{ position: "relative", display: "flex", alignItems: "baseline", gap: 6 }}>
        <span style={{ fontSize: 34, fontWeight: 500, color: t.fg0, letterSpacing: "-0.02em", fontFamily: FONT_MONO }}>
          {prefix}{fmtNum(value)}{suffix}
        </span>
      </div>
      {spark && spark.length > 1 && <div style={{ marginTop: -4 }}><Sparkline data={spark} color={toneColor} h={28} w={130} /></div>}
      {hint && (
        <div style={{ position: "relative", fontSize: 11, color: t.fg2, lineHeight: 1.4, marginTop: "auto", paddingTop: 4, borderTop: `1px dashed ${t.line}` }}>{hint}</div>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────
export default function ReportPage() {
  const reportMatch = window.location.pathname.match(/^\/report\/([a-f0-9]+)$/);
  const cabinetMatch = window.location.pathname.match(/^\/cabinet\/([a-z0-9_-]+)\/?$/i);
  const reportToken = reportMatch ? reportMatch[1] : null;
  const cabinetProjectId = cabinetMatch ? cabinetMatch[1] : null;
  const isCabinet = !!cabinetProjectId;
  // Менеджерське прев'ю: ?preview=<JWT> у URL → одразу сесія, без пароля.
  const previewToken = (() => { try { return new URLSearchParams(window.location.search).get("preview"); } catch { return null; } })();
  const isPreview = !!previewToken;

  const [rpDark, setRpDark] = useState(() => {
    const saved = localStorage.getItem("metago_dark");
    return saved !== null ? saved === "true" : true;
  });
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const toggleGroup = (url) => setExpandedGroups(prev => {
    const next = new Set(prev);
    if (next.has(url)) next.delete(url); else next.add(url);
    return next;
  });
  const t = themeFor(rpDark);

  const [rpPass, setRpPass] = useState("");
  const [rpError, setRpError] = useState("");
  const sessionKey = isCabinet ? ("spalah_cabinet_" + cabinetProjectId) : ("spalah_report_" + (reportToken || ""));
  const [rpSession, setRpSession] = useState(() => {
    if (previewToken) return previewToken;
    try { return localStorage.getItem(sessionKey) || null; } catch { return null; }
  });
  const [taskRange, setTaskRange] = useState("m1"); // вікно задач канбану: m1=30д / m2=60 / m3=90 / all
  const [rpData, setRpData] = useState(null);
  const [cityParam, setCityParam] = useState(""); // мультимісто: "" = головний (Київ), <id> = місто, "__all__" = агрегат
  const [rpLoading, setRpLoading] = useState(false);
  const [aiSummary, setAiSummary] = useState([]);
  const [wpExpanded, setWpExpanded] = useState(null);
  // Date override — клієнт може обрати дату зняття звіту вгорі сторінки
  const reportDateKey = "spalah_report_date_" + (reportToken || "");
  const [reportDate, setReportDate] = useState(() => {
    try { return localStorage.getItem(reportDateKey) || ""; } catch { return ""; }
  });
  const setReportDateAndPersist = (v) => {
    setReportDate(v);
    try { v ? localStorage.setItem(reportDateKey, v) : localStorage.removeItem(reportDateKey); } catch {}
  };
  const [wpTaskExp, setWpTaskExp] = useState({}); // { "monthIdx-taskIdx": true }
  const [openMonths, setOpenMonths] = useState(() => new Set()); // план робіт: місяці згорнуті за замовч.
  const [periodWin, setPeriodWin] = useState(30); // вікно періоду звіту в днях: 30/90/180/365
  const [cmpMode, setCmpMode] = useState("period"); // period | range | compare — режим дат
  const [cmpFrom, setCmpFrom] = useState(""); // ISO дата 1 / «від»
  const [cmpTo, setCmpTo] = useState("");     // ISO дата 2 / «до»
  const [posVer, setPosVer] = useState("main"); // перемикач мовної версії позицій: "main" | "v2"
  const toggleMonth = (i) => setOpenMonths(prev => { const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n; });
  const [kanban, setKanban] = useState(null); // { columns, tasks }
  const [taskModal, setTaskModal] = useState(null); // відкрита задача (клієнт може переглянути + PDF)
  const [tickets, setTickets] = useState([]);
  const [newTicket, setNewTicket] = useState({ title: "", description: "", open: false, sending: false });
  const [openTicketId, setOpenTicketId] = useState(null);
  const [ticketComments, setTicketComments] = useState({}); // ticketId → comment[]
  const [ticketReplyDraft, setTicketReplyDraft] = useState({}); // ticketId → draft
  const [ticketReplyBusy, setTicketReplyBusy] = useState(null);
  const [seoTexts, setSeoTexts] = useState([]);
  const [frag, setFrag] = useState(null); // { textId, quote, start, end } — selected fragment for a comment
  const [fragText, setFragText] = useState("");
  const [fragBusy, setFragBusy] = useState(false);
  const previewRef = useRef(null);
  const [openTextId, setOpenTextId] = useState(null);
  const [textComments, setTextComments] = useState({}); // id → draft (shared: review note + thread message)
  const [textBusyId, setTextBusyId] = useState(null);
  const [threadBusyId, setThreadBusyId] = useState(null);

  // Auth — cabinet mode uses email+password, report-link mode uses token+password
  const [rpEmail, setRpEmail] = useState("");
  const rpAuth = async (e) => {
    e.preventDefault();
    setRpError(""); setRpLoading(true);
    try {
      let sessionToken;
      if (isCabinet) {
        const r = await fetch("/api/cabinet/login", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ projectId: cabinetProjectId, email: rpEmail, password: rpPass }),
        });
        const d = await r.json();
        if (!r.ok || !d.token) { setRpError(d.error || "Auth failed"); setRpLoading(false); return; }
        sessionToken = d.token;
      } else {
        const r = await fetch("/api/report-public/auth", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: reportToken, password: rpPass }),
        });
        const d = await r.json();
        if (!r.ok || !d.sessionToken) { setRpError(d.error || "Auth failed"); setRpLoading(false); return; }
        sessionToken = d.sessionToken;
      }
      try { localStorage.setItem(sessionKey, sessionToken); } catch {}
      setRpSession(sessionToken); // дані підтягне ефект авто-завантаження (враховує обране місто)
    } catch { setRpError("Connection error"); }
    setRpLoading(false);
  };

  // Load kanban + tickets + seo-texts-for-review when session ready
  useEffect(() => {
    if (!rpSession) return;
    let cancel = false;
    fetch(`/api/report-public/kanban?taskRange=${taskRange}${cityParam && cityParam !== "__all__" ? `&cityId=${encodeURIComponent(cityParam)}` : ""}`, { headers: { Authorization: `Bearer ${rpSession}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!cancel && d) setKanban(d); })
      .catch(() => {});
    fetch("/api/ticket-client/list", { headers: { Authorization: `Bearer ${rpSession}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!cancel && Array.isArray(d)) setTickets(d); })
      .catch(() => {});
    fetch("/api/report-public/seo-texts", { headers: { Authorization: `Bearer ${rpSession}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!cancel && Array.isArray(d)) setSeoTexts(d); })
      .catch(() => {});
    return () => { cancel = true; };
  }, [rpSession, taskRange, cityParam]);

  const loadSeoTexts = () => {
    if (!rpSession) return;
    fetch("/api/report-public/seo-texts", { headers: { Authorization: `Bearer ${rpSession}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (Array.isArray(d)) setSeoTexts(d); })
      .catch(() => {});
  };

  const submitTextReview = async (id, decision) => {
    const comment = (textComments[id] || "").trim();
    if (decision === "rejected" && !comment) {
      alert("Будь ласка, напишіть коментар — що саме переписати.");
      return;
    }
    setTextBusyId(id);
    try {
      const r = await fetch(`/api/report-public/seo-texts/${id}/review`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${rpSession}` },
        body: JSON.stringify({ decision, comment }),
      });
      const d = await r.json();
      if (!r.ok) {
        // Stale list (e.g. status changed on the server since page load) — refresh and inform softly
        if (r.status === 404) {
          alert("Цей текст уже не очікує погодження — список оновлено.");
          loadSeoTexts();
          setOpenTextId(null);
        } else {
          alert(d.error || "Помилка");
        }
      } else { setTextComments(prev => ({ ...prev, [id]: "" })); loadSeoTexts(); setOpenTextId(null); }
    } catch (e) { alert(e.message); }
    setTextBusyId(null);
  };

  // Add a message to the conversation thread without changing status
  const submitThreadComment = async (id) => {
    const body = (textComments[id] || "").trim();
    if (!body) return;
    setThreadBusyId(id);
    try {
      const r = await fetch(`/api/report-public/seo-texts/${id}/comment`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${rpSession}` },
        body: JSON.stringify({ body }),
      });
      const d = await r.json();
      if (!r.ok) alert(d.error || "Помилка");
      else { setTextComments(prev => ({ ...prev, [id]: "" })); loadSeoTexts(); }
    } catch (e) { alert(e.message); }
    setThreadBusyId(null);
  };

  // ── Fragment comments: client selects a piece of the preview and comments on exactly that piece ──
  const captureSelection = (textId) => {
    try {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed) return;
      const cont = previewRef.current;
      if (!cont) return;
      const range = sel.getRangeAt(0);
      if (!cont.contains(range.startContainer) || !cont.contains(range.endContainer)) return;
      const quote = sel.toString().replace(/\s+/g, " ").trim();
      if (quote.length < 2) return;
      const a = charOffsetOf(cont, range.startContainer, range.startOffset);
      const b = charOffsetOf(cont, range.endContainer, range.endOffset);
      setFrag({ textId, quote: quote.slice(0, 2000), start: Math.min(a, b), end: Math.max(a, b) });
      setFragText("");
    } catch (e) {}
  };
  const submitFragmentComment = async (id) => {
    const body = fragText.trim();
    if (!body || !frag) return;
    setFragBusy(true);
    try {
      const r = await fetch(`/api/report-public/seo-texts/${id}/comment`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${rpSession}` },
        body: JSON.stringify({ body, anchor: { quote: frag.quote, start: frag.start, end: frag.end } }),
      });
      const d = await r.json();
      if (!r.ok) alert(d.error || "Помилка");
      else { setFrag(null); setFragText(""); loadSeoTexts(); }
    } catch (e) { alert(e.message); }
    setFragBusy(false);
  };
  useEffect(() => {
    const cont = previewRef.current;
    if (!cont || openTextId == null) return;
    const txt = seoTexts.find(x => x.id === openTextId);
    if (!txt) return;
    try {
      cont.querySelectorAll("mark[data-frag]").forEach(m => { const p = m.parentNode; while (m.firstChild) p.insertBefore(m.firstChild, m); p.removeChild(m); p.normalize(); });
      (txt.comments || []).forEach(c => {
        const an = c.anchor;
        if (an && Number.isFinite(an.start) && Number.isFinite(an.end) && an.end > an.start) highlightFragRange(cont, an.start, an.end);
      });
    } catch (e) {}
  }, [openTextId, seoTexts]);

  const submitClientTicket = async () => {
    if (!newTicket.title.trim()) return;
    setNewTicket(s => ({ ...s, sending: true }));
    try {
      const r = await fetch("/api/ticket-client/create", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${rpSession}` },
        body: JSON.stringify({ title: newTicket.title.trim(), description: newTicket.description.trim() }),
      });
      if (r.ok) {
        const created = await r.json();
        setTickets(prev => [created, ...prev]);
        setNewTicket({ title: "", description: "", open: false, sending: false });
      } else {
        setNewTicket(s => ({ ...s, sending: false }));
      }
    } catch { setNewTicket(s => ({ ...s, sending: false })); }
  };

  const openTicketModal = (id) => { setOpenTicketId(id); loadTicketComments(id); };
  const closeTicketModal = () => setOpenTicketId(null);
  const deleteTicket = async (id) => {
    if (!window.confirm("Видалити цей запит? Дію не можна скасувати.")) return;
    try {
      const r = await fetch(`/api/ticket-client/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${rpSession}` } });
      const d = await r.json();
      if (!r.ok) { alert(d.error || "Помилка"); return; }
      setTickets(prev => prev.filter(x => x.id !== id));
      setOpenTicketId(null);
    } catch (e) { alert(e.message); }
  };
  const loadTicketComments = (id) => {
    fetch(`/api/ticket-client/comments/${id}`, { headers: { Authorization: `Bearer ${rpSession}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (Array.isArray(d)) setTicketComments(prev => ({ ...prev, [id]: d })); })
      .catch(() => {});
  };
  const submitTicketComment = async (id) => {
    const text = (ticketReplyDraft[id] || "").trim();
    if (!text) return;
    setTicketReplyBusy(id);
    try {
      const r = await fetch(`/api/ticket-client/comment/${id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${rpSession}` },
        body: JSON.stringify({ text }),
      });
      const d = await r.json();
      if (!r.ok) alert(d.error || "Помилка");
      else { setTicketReplyDraft(prev => ({ ...prev, [id]: "" })); loadTicketComments(id); }
    } catch (e) { alert(e.message); }
    setTicketReplyBusy(null);
  };

  // Load AI summary once we have session
  useEffect(() => {
    if (!rpSession) return;
    let cancel = false;
    fetch("/api/report-public/ai-summary", { headers: { Authorization: `Bearer ${rpSession}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!cancel && d?.summary) setAiSummary(d.summary); })
      .catch(() => {});
    return () => { cancel = true; };
  }, [rpSession]);

  // Auto-load on session + refetch при зміні міста (мультимісто)
  useEffect(() => {
    if (!rpSession) return;
    let cancel = false;
    (async () => {
      setRpLoading(true);
      try {
        const url = "/api/report-public/data" + (cityParam ? `?cityId=${encodeURIComponent(cityParam)}` : "");
        const dr = await fetch(url, { headers: { Authorization: `Bearer ${rpSession}` } });
        if (cancel) return;
        if (dr.ok) {
          const dd = await dr.json();
          setRpData(dd);
        } else if (dr.status === 401) {
          try { localStorage.removeItem(sessionKey); } catch {}
          setRpSession(null);
        }
      } catch {}
      if (!cancel) setRpLoading(false);
    })();
    return () => { cancel = true; };
  }, [rpSession, cityParam, sessionKey]);

  // ─── Password gate ──────────────────────────────────────────────
  if (!rpSession) {
    return (
      <div style={{ fontFamily: "'DM Sans', -apple-system, sans-serif", display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", background: t.bg0, color: t.fg0 }}>
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap" rel="stylesheet" />
        <div style={{ width: 360, padding: 32, background: t.bg1, borderRadius: 16, border: `1px solid ${t.line}`, boxShadow: rpDark ? "0 4px 24px rgba(0,0,0,.4)" : "0 4px 24px rgba(0,0,0,.08)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 24 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: `linear-gradient(135deg, ${PAL.brand}, ${PAL.brand2})`, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 800, fontSize: 14 }}>S</div>
            <span style={{ fontWeight: 800, fontSize: 18 }}>Spalah</span>
          </div>
          <p style={{ textAlign: "center", fontSize: 13, color: t.fg2, marginBottom: 16 }}>SEO Report</p>
          <form onSubmit={rpAuth}>
            {isCabinet && (
              <input type="email" placeholder="Email" value={rpEmail} onChange={e => setRpEmail(e.target.value)} required
                style={{ border: `1px solid ${t.line}`, borderRadius: 8, padding: "10px 14px", fontSize: 14, outline: "none", width: "100%", fontFamily: "inherit", background: t.bg2, color: t.fg0, boxSizing: "border-box", marginBottom: 10 }} />
            )}
            <input type="password" placeholder="Password" value={rpPass} onChange={e => setRpPass(e.target.value)}
              style={{ border: `1px solid ${t.line}`, borderRadius: 8, padding: "10px 14px", fontSize: 14, outline: "none", width: "100%", fontFamily: "inherit", background: t.bg2, color: t.fg0, boxSizing: "border-box", marginBottom: 12 }} />
            <button type="submit" disabled={rpLoading} style={{ background: `linear-gradient(135deg, ${PAL.brand}, ${PAL.brand2})`, color: "#fff", border: "none", borderRadius: 8, padding: "10px 16px", fontSize: 14, fontWeight: 600, cursor: "pointer", width: "100%" }}>
              {rpLoading ? "..." : "Open Report"}
            </button>
            {rpError && <p style={{ color: PAL.neg, fontSize: 12, marginTop: 8, textAlign: "center" }}>{rpError}</p>}
          </form>
        </div>
      </div>
    );
  }

  if (!rpData) {
    return (
      <div style={{ fontFamily: "'DM Sans', sans-serif", display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", background: t.bg0, color: t.fg0 }}>
        <div style={{ fontSize: 14 }}>Завантаження звіту...</div>
      </div>
    );
  }

  // ─── Мультимісто: перемикач міст + агрегат (напр. Sushi Master 38 міст) ──
  const _cities = Array.isArray(rpData.cities) ? rpData.cities : [];
  const _activeCity = rpData.activeCityId || null;
  const _isGroup = !!rpData.isGroup && _cities.length > 1;
  const switchCity = (cid) => { if ((cid || "") === cityParam) return; setRpData(null); setKanban(null); setCityParam(cid || ""); };
  const _nf = (n) => (Number(n) || 0).toLocaleString("uk-UA");
  const CitySwitcher = _isGroup ? (
    <div className="rp-noprint" data-blk="_cities" style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center", padding: "10px 12px", borderRadius: 12, border: `1px solid ${t.line}`, background: t.bg1 }}>
      <span style={{ fontSize: 12, fontWeight: 800, color: t.fg2, marginRight: 4 }}>🏙 Місто:</span>
      {[{ id: "__all__", city: "Усі міста", isMain: false, all: true }, ..._cities].map((c) => {
        const active = c.all ? _activeCity === "__all__" : _activeCity === c.id;
        return (
          <button key={c.id} onClick={() => switchCity(c.all ? "__all__" : c.id)}
            style={{ fontSize: 12, fontWeight: 700, padding: "4px 11px", borderRadius: 7, cursor: "pointer",
              border: `1.5px solid ${active ? PAL.brand : t.line}`,
              background: active ? `linear-gradient(135deg, ${PAL.brand}, ${PAL.brand2})` : "transparent",
              color: active ? "#fff" : t.fg1 }}>
            {c.isMain ? "★ " : ""}{c.city}
          </button>
        );
      })}
    </div>
  ) : null;

  // Агрегат «усі міста» — зведена сторінка (KPI + таблиця по містах)
  if (rpData.aggregate) {
    const a = rpData.aggregate;
    const T = a.totals || {};
    const dClicks = a.prev?.hasPrev ? (T.clicks - a.prev.clicks) : null;
    const dTop10 = a.prev?.hasPrev ? (T.top10 - a.prev.top10) : null;
    const card = (label, val, delta) => (
      <div style={{ flex: "1 1 150px", minWidth: 140, background: t.bg1, border: `1px solid ${t.line}`, borderRadius: 14, padding: "16px 18px" }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: t.fg2 }}>{label}</div>
        <div style={{ fontSize: 26, fontWeight: 800, marginTop: 4 }}>{_nf(val)}</div>
        {delta != null && <div style={{ fontSize: 12, fontWeight: 700, marginTop: 2, color: delta >= 0 ? PAL.pos : PAL.neg }}>{delta >= 0 ? "▲ +" : "▼ "}{_nf(Math.abs(delta))}</div>}
      </div>
    );
    const th = { textAlign: "right", padding: "8px 10px", fontSize: 11, fontWeight: 700, color: t.fg2, textTransform: "uppercase", borderBottom: `2px solid ${t.line}` };
    const td = { textAlign: "right", padding: "8px 10px", fontSize: 13, borderBottom: `1px solid ${t.line}` };
    return (
      <div style={{ fontFamily: "'DM Sans', -apple-system, sans-serif", minHeight: "100vh", background: t.bg0, color: t.fg0, padding: "24px 20px" }}>
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
        <div style={{ maxWidth: 1120, margin: "0 auto", display: "flex", flexDirection: "column", gap: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: `linear-gradient(135deg, ${PAL.brand}, ${PAL.brand2})`, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 800 }}>S</div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 20 }}>Sushi Master — усі міста</div>
              <div style={{ fontSize: 12, color: t.fg2 }}>Зведений звіт по {a.cityCount} містах · дані Google Search Console</div>
            </div>
          </div>
          {CitySwitcher}
          {rpData.reportComment ? <div style={{ background: t.bg1, border: `1px solid ${t.line}`, borderRadius: 12, padding: "12px 16px", fontSize: 14, lineHeight: 1.6 }}>{rpData.reportComment}</div> : null}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            {card("Запити (GSC)", T.queries)}
            {card("Кліки / 28 дн", T.clicks, dClicks)}
            {card("Покази / 28 дн", T.impressions)}
            {card("ТОП-3", T.top3)}
            {card("ТОП-10", T.top10, dTop10)}
            {card("ТОП-30", T.top30)}
          </div>
          <div style={{ background: t.bg1, border: `1px solid ${t.line}`, borderRadius: 14, padding: "8px 10px", overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 680 }}>
              <thead><tr>
                <th style={{ ...th, textAlign: "left" }}>Місто</th>
                <th style={th}>Запити</th><th style={th}>Кліки</th><th style={th}>Покази</th>
                <th style={th}>ТОП-3</th><th style={th}>ТОП-10</th><th style={th}>ТОП-30</th>
              </tr></thead>
              <tbody>
                {(a.rows || []).map((r) => (
                  <tr key={r.id} style={{ cursor: "pointer" }} onClick={() => switchCity(r.id)} title="Відкрити звіт міста">
                    <td style={{ ...td, textAlign: "left", fontWeight: 700, color: PAL.brand2 }}>{r.city}</td>
                    <td style={td}>{_nf(r.queries)}</td><td style={td}>{_nf(r.clicks)}</td><td style={td}>{_nf(r.impressions)}</td>
                    <td style={td}>{_nf(r.top3)}</td><td style={td}>{_nf(r.top10)}</td><td style={td}>{_nf(r.top30)}</td>
                  </tr>
                ))}
                <tr style={{ fontWeight: 800 }}>
                  <td style={{ ...td, textAlign: "left" }}>Разом</td>
                  <td style={td}>{_nf(T.queries)}</td><td style={td}>{_nf(T.clicks)}</td><td style={td}>{_nf(T.impressions)}</td>
                  <td style={td}>{_nf(T.top3)}</td><td style={td}>{_nf(T.top10)}</td><td style={td}>{_nf(T.top30)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: 11, color: t.fg2 }}>Натисніть на місто, щоб відкрити його детальний звіт.</div>
        </div>
      </div>
    );
  }

  // ─── Derive ─────────────────────────────────────────────────────
  const p = rpData.project || {};
  const gsc = rpData.gsc || {};
  const todayISO = new Date().toISOString().slice(0, 10);
  // Анкер (верхня межа вікна): обрана «Дата звіту» (включно) або вчора (сьогодні не рахуємо).
  // Тоді 30/90/180/365 днів рахуються НАЗАД від обраної дати, а не від сьогодні.
  const anchorISO = (reportDate && /^\d{4}-\d{2}-\d{2}$/.test(reportDate)) ? reportDate : null;
  const upToAnchor = (d) => { const dt = String(d || "").slice(0, 10); if (!dt) return true; return anchorISO ? dt <= anchorISO : dt < todayISO; };
  // Для ЗНІМКІВ (позиції/GSC-лічильники) сьогодні ВКЛЮЧАЄМО — знімок це повний підрахунок на момент,
  // а не неповний денний трафік. Інакше єдиний сьогоднішній знімок відфільтровувався → TOP-10=0.
  const upToSnap = (d) => { const dt = String(d || "").slice(0, 10); if (!dt) return true; return dt <= (anchorISO || todayISO); };
  const dailyArr = (Array.isArray(gsc.daily) ? gsc.daily : []).filter(d => upToAnchor(d.date));
  // Мовні версії позицій (RU/UA) в одному звіті. Перемикач posVer міняє джерело — і графік, і ключі.
  const _posVerAvail = Array.isArray(rpData.positionSnapsV2) && rpData.positionSnapsV2.length > 0;
  const _useV2 = _posVerAvail && posVer === "v2";
  const positionSnaps = _useV2
    ? rpData.positionSnapsV2
    : (Array.isArray(rpData.positionSnaps) ? rpData.positionSnaps : []);
  const gscSnapsRaw = Array.isArray(gsc.snapshots) ? gsc.snapshots : [];
  // GSC snapshots use *_count + total_queries — normalize to position-schema so KPI math is uniform.
  const gscSnaps = gscSnapsRaw.map(s => ({
    ...s,
    date: s.snapshot_date,
    top1: s.top1_count || 0,
    top3: s.top3_count || 0,
    top5: s.top5_count || 0,
    top10: s.top10_count || 0,
    top30: s.top30_count || 0,
    top50: s.top50_count || 0,
    top100: s.top100_count || 0,
    totalQueries: s.total_queries || 0,
  }));
  // Джерело для «Динаміки позицій»: якщо є внутрішні знімки позицій (модуль Позицій) — завжди їх.
  // GSC — лише запасний варіант коли position_snapshots взагалі порожні.
  // якщо anchor-дата відфільтровує ВСІ position-знімки (трекінг почали після дати звіту) — ігноруємо anchor для позицій
  const _posFiltered = positionSnaps.filter(x => upToSnap(x.snapshot_date || x.date));
  const snaps = _posFiltered.length > 0
    ? _posFiltered
    : positionSnaps.length > 0
      ? positionSnaps
      : gscSnaps.filter(x => upToSnap(x.snapshot_date || x.date));
  const topQ = Array.isArray(gsc.topQueries) ? gsc.topQueries : [];
  const keywordGroups = _useV2
    ? (Array.isArray(rpData.keywordGroupsV2) ? rpData.keywordGroupsV2 : [])
    : (Array.isArray(rpData.keywordGroups) ? rpData.keywordGroups : []);
  // ВЧ (високочастотні) ключі — перемикаються тією ж кнопкою RU/UA (posVer)
  const hfSnaps = _useV2
    ? (Array.isArray(rpData.hfSnapsV2) ? rpData.hfSnapsV2 : [])
    : (Array.isArray(rpData.hfSnaps) ? rpData.hfSnaps : []);
  const hfKeywords = _useV2
    ? (Array.isArray(rpData.hfKeywordsV2) ? rpData.hfKeywordsV2 : [])
    : (Array.isArray(rpData.hfKeywords) ? rpData.hfKeywords : []);
  const hfMinFreq = rpData.hfMinFreq || 1000;
  const hfLast = hfSnaps.at(-1) || null;
  const competitors = Array.isArray(rpData.competitors) ? rpData.competitors : [];
  const serp = Array.isArray(rpData.serpstat) ? rpData.serpstat : [];

  // KPI calcs — selectable window (N days) vs previous N days
  const N = periodWin;
  const hasPrevWin = dailyArr.length >= 2 * N; // достатньо історії для повного попереднього вікна
  const curWin = dailyArr.slice(-N);
  const prevWin = hasPrevWin ? dailyArr.slice(-2 * N, -N) : [];
  const last30Clicks = curWin.reduce((s, d) => s + (d.clicks || 0), 0);
  const prev30Clicks = prevWin.reduce((s, d) => s + (d.clicks || 0), 0);
  const last30Imp = curWin.reduce((s, d) => s + (d.impressions || 0), 0);
  const prev30Imp = prevWin.reduce((s, d) => s + (d.impressions || 0), 0);
  const sparkClicks = curWin.map(d => d.clicks || 0);
  const sparkImp = curWin.map(d => d.impressions || 0);

  // Snapshots (snaps sorted ASC oldest→newest). Порівнюємо ОСТАННІЙ знімок із найранішим
  // У МЕЖАХ обраного періоду N днів (30→останній vs ~30 дн тому; 90→останній vs найраніший за 90 дн).
  // winStart = (анкер або сьогодні) − N днів.
  const _winStartISO = new Date(new Date((anchorISO || todayISO) + "T00:00:00Z").getTime() - N * 86400000).toISOString().slice(0, 10);
  let winSnaps = snaps.filter(s => String(s.snapshot_date || s.date || "").slice(0, 10) >= _winStartISO);
  let latest = winSnaps.at(-1) || snaps.at(-1) || null; // якщо у вікні порожньо — хоч поточне значення
  // База для дельти: найраніший знімок У ВІКНІ, але лише якщо у вікні є ≥2 знімків.
  // Якщо у вікні 0–1 знімків — порівняння з самим собою давало б хибне «▲ 0%».
  // Тоді беремо ПОПЕРЕДНІЙ знімок у повній серії (реальна база), інакше найраніший загалом.
  let oldest = winSnaps.length >= 2 ? winSnaps[0] : null;
  if (latest && !oldest && snaps.length >= 2) {
    const li = snaps.lastIndexOf(latest);
    oldest = (li > 0 ? snaps[li - 1] : null) || snaps[0] || null;
  }
  // Два порівняння для карток позицій:
  //  • «Останній період» = останній знімок vs попередній (snaps[-1] vs snaps[-2])
  //  • «Весь період»     = перший знімок vs останній    (snaps[-1] vs snaps[0])
  const snapPct = (snap, key) => (snap && snap.totalQueries > 0) ? Math.round(((snap[key] || 0) / snap.totalQueries) * 100) : 0;
  let sLast = snaps.at(-1) || null;
  let sPrev = snaps.length >= 2 ? snaps[snaps.length - 2] : null;
  let sFirst = snaps.length >= 2 ? snaps[0] : null;
  // ── Date comparison (range / 2 dates) — overrides latest/oldest/sLast/sFirst → whole report reflects it ──
  const _snapDates = [...new Set(snaps.map(x => String(x.snapshot_date || x.date || "").slice(0, 10)).filter(Boolean))].sort();
  const _cmpActive = (cmpMode === "range" || cmpMode === "compare") && cmpFrom && cmpTo;
  if (_cmpActive) {
    const eA = cmpFrom < cmpTo ? cmpFrom : cmpTo, eB = cmpFrom < cmpTo ? cmpTo : cmpFrom;
    const _dateOf = (x) => String(x.snapshot_date || x.date || "").slice(0, 10);
    const inRange = snaps.filter(x => { const d = _dateOf(x); return d >= eA && d <= eB; });
    const findExact = (dd) => snaps.find(x => _dateOf(x) === dd);
    const snapA = findExact(eA) || inRange[0] || null, snapB = findExact(eB) || inRange.at(-1) || null;
    if (snapA && snapB) { winSnaps = inRange.length ? inRange : [snapA, snapB]; latest = snapB; oldest = snapA; sLast = snapB; sPrev = snapA; sFirst = snapA; }
  }
  const top10Now = latest?.top10 || 0, top10Old = oldest?.top10 || 0;
  const top3Now = latest?.top3 || 0, top3Old = oldest?.top3 || 0;
  const top30Now = latest?.top30 || 0, top30Old = oldest?.top30 || 0;
  const top100Now = latest?.top100 || 0, top100Old = oldest?.top100 || 0;
  // 30d positions average vs prev 30d
  const avgField = (arr, k) => arr.length ? Math.round(arr.reduce((s, x) => s + (x[k] || 0), 0) / arr.length) : 0;
  const last30Snaps = snaps.slice(-N);
  const prev30Snaps = snaps.slice(-2 * N, -N);
  const posAvg = {
    top1:  { now: avgField(last30Snaps, "top1"), prev: avgField(prev30Snaps, "top1") },
    top5:  { now: avgField(last30Snaps, "top5"), prev: avgField(prev30Snaps, "top5") },
    top3:  { now: avgField(last30Snaps, "top3"),   prev: avgField(prev30Snaps, "top3") },
    top10: { now: avgField(last30Snaps, "top10"),  prev: avgField(prev30Snaps, "top10") },
    top30: { now: avgField(last30Snaps, "top30"),  prev: avgField(prev30Snaps, "top30") },
    top100:{ now: avgField(last30Snaps, "top100"), prev: avgField(prev30Snaps, "top100") },
  };
  const totalQ = latest?.totalQueries || 0, totalQOld = oldest?.totalQueries || 0;
  const recentSnaps = _cmpActive ? winSnaps.slice(-60) : snaps.slice(-12);
  const sparkTop10 = recentSnaps.map(s => s.top10 || 0);
  const sparkTop3 = recentSnaps.map(s => s.top3 || 0);
  const sparkQ = recentSnaps.map(s => s.totalQueries || 0);

  // Ref domains — prefer OpenSEO (current truth, single snapshot → no delta/sparkline).
  const osRefDomains = rpData.openseoLinks?.referring_domains;
  const useOpenseoRef = osRefDomains != null;
  const refLatest = useOpenseoRef ? osRefDomains : (rpData.serpstat?.length ? serp.at(-1)?.total_ref_domains : 0);
  const refOldVal = useOpenseoRef ? refLatest : (serp[0]?.total_ref_domains || 0);
  const refDelta = useOpenseoRef ? null : (serp.length >= 2 ? deltaPct(refLatest, refOldVal) : null);
  const sparkRef = useOpenseoRef ? [] : serp.slice(-12).map(s => s.total_ref_domains || 0);

  // GA4
  const ga4 = rpData.ga4 || null;

  // Workplan, pagespeed, backlinks
  const workplan = Array.isArray(rpData.workplan)
    ? rpData.workplan
    : (rpData.workplan && Array.isArray(rpData.workplan.months) ? rpData.workplan.months : []);
  // ─── Конфіг блоків звіту (керує менеджер: вмик/вимк + порядок) ───
  const RP_BLOCKS = ["value","workplan","kanban","seoTexts","tickets","kpi","cwv","backlinks","ga4","gscTraffic","positions","keywordGroups","hfKeywords","coverage","aiVis"];
  const _cfgBlocks = Array.isArray(rpData.reportConfig?.blocks) ? rpData.reportConfig.blocks : null;
  const blkOn = (k) => { if (!_cfgBlocks) return true; const b = _cfgBlocks.find(x => x.key === k); return b ? b.enabled !== false : true; };
  const _blkOrder = _cfgBlocks ? _cfgBlocks.map(b => b.key) : RP_BLOCKS;
  // CSS: порядок (flex-контейнер секцій уже flex-column) + приховування вимкнених блоків.
  // ВАЖЛИВО: display:none мусить бути !important — секції мають inline style={{display:"flex"}},
  // а inline-стиль перебиває звичайний CSS зі <style>; !important перебиває inline.
  const FIXED_ORDER = { value: -5 };
  const blkStyleCss = `[data-blk="_hero"]{order:-10}[data-blk="_footer"]{order:1000}` +
    RP_BLOCKS.map(k => { const oi = FIXED_ORDER[k] != null ? FIXED_ORDER[k] : (_blkOrder.indexOf(k) < 0 ? 500 : _blkOrder.indexOf(k)); return `[data-blk="${k}"]{order:${oi}${blkOn(k) ? "" : ";display:none!important"}}`; }).join("");
  const reportComment = rpData.reportComment || "";
  const aiVis = rpData.aiVis || null;
  // GA4: ріжемо подобовий timeseries під обраний період (periodWin), cap на наявні дні знімку.
  // sessions/users/pageviews — реальні поденні; решта (newUsers/engaged/conversions) подобових нема → лишаємо за повний знімок.
  // Сьогоднішній (неповний) день у графік/суми НЕ тягнемо (дати в timeseries формату YYYYMMDD).
  const _todayYMD = todayISO.replace(/-/g, "");
  const _ga4ts = (Array.isArray(rpData.ga4?.timeseries) ? rpData.ga4.timeseries : []).filter(d => String(d.date || "").slice(0, 8) < _todayYMD);
  const ga4Days = _ga4ts.length ? Math.min(periodWin, _ga4ts.length) : (rpData.ga4?.days || 30);
  const ga4slice = _ga4ts.length ? _ga4ts.slice(-ga4Days) : _ga4ts;
  const ga4Sliced = _ga4ts.length > 0 && ga4Days < _ga4ts.length;
  const ga4Sum = (k) => ga4slice.reduce((s, d) => s + (Number(d[k]) || 0), 0);
  const ga4FullDays = _ga4ts.length || (rpData.ga4 && rpData.ga4.days) || 90; // повний період знімку (для агрегат-блоків без подобових даних)
  // ── Value block (money language): ad-equivalent + organic conversions ──
  const _orgConv = ga4 && ga4.cur ? Number(ga4.cur.conversions) : null;
  const _orgConvPrev = ga4 && ga4.prev ? Number(ga4.prev.conversions) : null;
  const _orgSess = ga4 && ga4.cur ? (Number(ga4.cur.sessions) || 0) : 0;
  const _convSuspect = _orgConv != null && _orgSess > 0 && _orgConv > _orgSess * 0.8;
  const _convDelta = (_orgConv != null && _orgConvPrev) ? deltaPct(_orgConv, _orgConvPrev) : null;
  const showValueBlk = _orgConv != null && !_convSuspect && _orgConv > 0;
  const ps = rpData.pagespeed?.mobile || rpData.pagespeed?.desktop || null;
  // Backlinks: prefer OpenSEO (fresh, manual pull) over the legacy Ahrefs/serpstat snapshot.
  const os = rpData.openseoLinks || null;
  const blSource = os ? "openseo" : (rpData.backlinks ? "ahrefs" : null);
  const bl = os ? {
    total: Number(os.backlinks) || 0,
    refDomains: Number(os.referring_domains) || 0,
    dr: Number(os.rank) || 0,
    spamScore: os.spam_score != null ? Number(os.spam_score) : null,
    brokenBacklinks: os.broken_backlinks != null ? Number(os.broken_backlinks) : null,
    fetchedAt: os.fetched_at || null,
    totalDelta: 0, refDomainsDelta: 0, drDelta: 0,
  } : (rpData.backlinks || null);

  // AI narrator helper
  const aiFor = (section) => aiSummary.find(s => s.section === section);

  // Coverage
  const cov = Array.isArray(rpData.coverage) ? rpData.coverage : [];
  const covMap = {};
  cov.forEach(c => { covMap[String(c.verdict || "").toUpperCase()] = parseInt(c.cnt, 10) || 0; });
  const indexed = covMap.PASS || 0;
  const coverageTotal = cov.reduce((s, c) => s + (parseInt(c.cnt, 10) || 0), 0);

  const top10Delta = deltaPct(top10Now, top10Old);
  const clicksDelta = hasPrevWin ? deltaPct(last30Clicks, prev30Clicks) : null;
  const imprDelta = hasPrevWin ? deltaPct(last30Imp, prev30Imp) : null;
  const queriesDelta = deltaPct(totalQ, totalQOld);

  const period = (() => {
    const w = dailyArr.slice(-N);
    if (!w.length) return "—";
    const first = w[0]?.date, last = w.at(-1)?.date;
    if (!first || !last) return "—";
    return `${fmtDate(first)} — ${fmtDate(last)}`;
  })();

  const RpToggle = () => (
    <div onClick={() => setRpDark(d => { localStorage.setItem("metago_dark", String(!d)); return !d; })} style={{
      width: 44, height: 24, borderRadius: 12, padding: 2, cursor: "pointer",
      background: rpDark ? PAL.brand : "#d0d0d8", transition: "background .3s",
      display: "flex", alignItems: "center", flexShrink: 0,
    }}>
      <div style={{ width: 20, height: 20, borderRadius: 10, background: "#fff", transform: rpDark ? "translateX(20px)" : "translateX(0)", transition: "transform .3s" }} />
    </div>
  );

  // ─── Render ─────────────────────────────────────────────────────
  return (
    <div style={{ position: "relative", zIndex: 1, background: t.bg0, color: t.fg0, fontFamily: "'DM Sans', -apple-system, sans-serif", minHeight: "100vh" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap" rel="stylesheet" />

      {/* ─── Header (sticky) ──────────────────────────────────── */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "14px 28px", borderBottom: `1px solid ${t.line}`,
        position: "sticky", top: 0, zIndex: 20,
        background: rpDark ? "rgba(17,17,29,0.88)" : "rgba(255,255,255,0.88)",
        backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
      }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <div style={{ width: 30, height: 30, borderRadius: 8, background: `linear-gradient(135deg, ${PAL.brand} 0%, ${PAL.brand2} 100%)`, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: 13 }}>S</div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: t.fg0 }}>{p.name || "—"}</div>
            <div style={{ fontSize: 10.5, color: t.fg2, fontFamily: FONT_MONO }}>{p.domain || ""}</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 16, alignItems: "center", fontSize: 11.5, color: t.fg2, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 10, color: t.fg2, opacity: 0.7 }}>Дата звіту:</span>
            <input
              type="date"
              value={reportDate}
              onChange={(e) => setReportDateAndPersist(e.target.value)}
              style={{
                fontFamily: FONT_MONO, fontSize: 11.5, color: t.fg1,
                background: "transparent", border: `1px solid ${t.line}`,
                borderRadius: 6, padding: "3px 6px", outline: "none",
                colorScheme: rpDark ? "dark" : "light",
              }}
            />
            {reportDate && (
              <button onClick={() => setReportDateAndPersist("")} title="Скинути"
                style={{ background: "transparent", border: 0, color: t.fg2, cursor: "pointer", fontSize: 13, padding: "0 2px" }}>×</button>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
            {[{ v: 30, l: "30 днів" }, { v: 90, l: "90 днів" }, { v: 180, l: "Пів року" }, { v: 365, l: "Рік" }].map(o => (
              <button key={o.v} onClick={() => setPeriodWin(o.v)} title={`Порівняння ${o.l} з попереднім таким же періодом`} style={{
                fontSize: 10.5, fontFamily: FONT_MONO, padding: "3px 9px", borderRadius: 6, cursor: "pointer", whiteSpace: "nowrap",
                border: `1px solid ${periodWin === o.v ? PAL.brand : t.line}`,
                background: periodWin === o.v ? PAL.brand : "transparent",
                color: periodWin === o.v ? "#fff" : t.fg2, fontWeight: periodWin === o.v ? 600 : 400,
              }}>{o.l}</button>
            ))}
          </div>
          <div className="rp-noprint" style={{ display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap" }}>
            {[{ v: "period", l: "Період" }, { v: "range", l: "Діапазон" }, { v: "compare", l: "Порівняти 2 дати" }].map(o => (
              <button key={o.v} onClick={() => setCmpMode(o.v)} style={{
                fontSize: 10.5, fontFamily: FONT_MONO, padding: "3px 9px", borderRadius: 6, cursor: "pointer", whiteSpace: "nowrap",
                border: `1px solid ${cmpMode === o.v ? PAL.brand2 : t.line}`,
                background: cmpMode === o.v ? PAL.brand2 : "transparent",
                color: cmpMode === o.v ? "#fff" : t.fg2, fontWeight: cmpMode === o.v ? 600 : 400,
              }}>{o.l}</button>
            ))}
            {cmpMode !== "period" && [{ val: cmpFrom, set: setCmpFrom, ph: cmpMode === "compare" ? "Дата 1" : "Від" }, { val: cmpTo, set: setCmpTo, ph: cmpMode === "compare" ? "Дата 2" : "До" }].map((d, di) => (
              <select key={di} value={d.val} onChange={(e) => d.set(e.target.value)} style={{
                fontFamily: FONT_MONO, fontSize: 10.5, color: t.fg1, background: "transparent",
                border: `1px solid ${t.line}`, borderRadius: 6, padding: "3px 5px", outline: "none", colorScheme: rpDark ? "dark" : "light",
              }}>
                <option value="">{d.ph}</option>
                {_snapDates.map(sd => <option key={sd} value={sd}>{fmtDate(sd)}</option>)}
              </select>
            ))}
          </div>
          <span style={{ fontFamily: FONT_MONO, opacity: 0.7 }}>· {_cmpActive ? `${fmtDate(cmpFrom)} → ${fmtDate(cmpTo)}` : period}{!hasPrevWin ? " · без порівняння (мало історії)" : ""}</span>
          {isPreview && <span className="rp-noprint" style={{ fontSize: 10.5, fontWeight: 700, padding: "2px 8px", borderRadius: 5, background: hexA(PAL.warn, .18), color: PAL.warn }}>ПРЕВ'Ю</span>}
          <button className="rp-noprint" onClick={() => window.print()} title="Зберегти/роздрукувати звіт у PDF" style={{
            fontSize: 10.5, fontFamily: FONT_MONO, padding: "3px 10px", borderRadius: 6, cursor: "pointer",
            border: `1px solid ${t.line}`, background: "transparent", color: t.fg2,
          }}>🖨 PDF</button>
          <RpToggle />
        </div>
      </div>
      <style>{`@media print { .rp-noprint{display:none!important} [style*="position: sticky"]{position:static!important} body{background:#fff!important} }`}</style>

      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "32px 28px 80px", display: "flex", flexDirection: "column", gap: 32 }}>
        <style>{blkStyleCss}</style>

        {CitySwitcher}

        {/* ─── HERO ─────────────────────────────────────────── */}
        <section data-blk="_hero">
          <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: PAL.brand2, letterSpacing: "0.18em" }}>SEO REPORT</span>
            {top10Delta != null && <ClientChip kind={top10Delta >= 0 ? "pos" : "neg"} t={t}>{top10Delta >= 0 ? "+" : ""}{top10Delta}% TOP-10</ClientChip>}
            {clicksDelta != null && <ClientChip kind={clicksDelta >= 0 ? "pos" : "neg"} t={t}>{clicksDelta >= 0 ? "+" : ""}{clicksDelta}% кліки</ClientChip>}
            {imprDelta != null && <ClientChip kind={imprDelta >= 0 ? "pos" : "neg"} t={t}>{imprDelta >= 0 ? "+" : ""}{imprDelta}% покази</ClientChip>}
          </div>
          <h1 style={{
            fontSize: "clamp(36px, 5.5vw, 68px)", fontWeight: 600, letterSpacing: "-0.035em",
            margin: "0 0 16px", lineHeight: 1.0, maxWidth: 1100,
            background: `linear-gradient(180deg, ${t.fg0} 30%, ${PAL.brand2} 90%)`,
            WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent",
          }}>
            {top10Delta != null && top10Delta > 0
              ? <>Запитів у TOP-10 зросло на <span>{top10Delta}%</span>.</>
              : <>SEO-звіт по проекту {p.name}.</>}
          </h1>
          <p style={{ fontSize: 16, color: t.fg1, maxWidth: 760, lineHeight: 1.55, margin: 0 }}>
            {top10Now && top10Old != null ? (
              top10Now > top10Old
                ? <>Кількість запитів у TOP-10 зросла з <strong style={{ color: t.fg0 }}>{top10Old}</strong> до <strong style={{ color: PAL.pos }}>{top10Now}</strong>.</>
                : top10Now < top10Old
                  ? <>Кількість запитів у TOP-10 змінилася з <strong style={{ color: t.fg0 }}>{top10Old}</strong> до <strong style={{ color: PAL.neg }}>{top10Now}</strong>.</>
                  : <>Кількість запитів у TOP-10 лишилася на рівні <strong style={{ color: t.fg0 }}>{top10Now}</strong>.</>
            ) : null}{" "}
            {imprDelta != null && imprDelta > 0 ? <>Покази в Google зросли на <strong style={{ color: PAL.pos }}>+{imprDelta}%</strong>.</> : null}
          </p>
        </section>

        {/* ─── КОМЕНТАР МЕНЕДЖЕРА ─────────────────────────── */}
        {reportComment && (
          <section style={{ order: -5, background: rpDark ? "rgba(99,102,241,.08)" : "rgba(99,102,241,.06)", border: `1px solid ${t.line}`, borderRadius: 14, padding: "18px 22px" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: PAL.brand2, letterSpacing: "0.14em", marginBottom: 8 }}>КОМЕНТАР КОМАНДИ</div>
            <div style={{ fontSize: 15, color: t.fg1, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{reportComment}</div>
          </section>
        )}

        {/* ─── ПЛАН РОБІТ ─────────────────────────────────── */}
        {workplan.length > 0 && (() => {
          const totalTasks = workplan.reduce((s, m) => s + (m.tasks?.length || 0), 0);
          const totalDone = workplan.reduce((s, m) => s + (m.tasks?.filter(tk => tk.done || tk.status === "done").length || 0), 0);
          const totalPct = totalTasks > 0 ? Math.round((totalDone / totalTasks) * 100) : 0;
          const mPct = (m) => { const tot = (m.tasks||[]).length; if (!tot) return 0; return Math.round(((m.tasks||[]).filter(tk=>tk.done||tk.status==="done").length/tot)*100); };
          const cfgCurMonth = rpData.reportConfig?.currentMonth;
          let pastPart, currPart, futurePart;
          if (cfgCurMonth) {
            const curIdx = cfgCurMonth - 1;
            pastPart = workplan.slice(0, curIdx);
            currPart = workplan[curIdx] ? [workplan[curIdx]] : [];
            futurePart = workplan.slice(curIdx + 1);
          } else {
            pastPart = workplan.filter(m => mPct(m) === 100);
            currPart = [];
            futurePart = workplan.filter(m => mPct(m) < 100);
          }

          const renderMonthGrid = (months) => (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}>
              {months.map((m, i) => {
                const done = (m.tasks || []).filter(tk => tk.done || tk.status === "done").length;
                const total = (m.tasks || []).length;
                const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                const status = pct === 100 ? "done" : pct > 0 ? "active" : "queued";
                const c = status === "done" ? PAL.pos : status === "active" ? PAL.brand2 : t.fg3;
                const bg = status === "done" ? `${PAL.pos}10` : status === "active" ? `${PAL.brand2}10` : "transparent";
                const origNum = m.month || (workplan.indexOf(m) + 1);
                return (
                  <div key={i} style={{
                    padding: 14, background: bg,
                    borderRight: `1px solid ${t.line}`, borderTop: `1px solid ${t.line}`,
                    display: "flex", flexDirection: "column", gap: 6,
                    transition: "background .15s",
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 10, fontFamily: FONT_MONO, color: c, fontWeight: 700, letterSpacing: "0.1em" }}>М{origNum}</span>
                      {status === "done" && <span style={{ color: PAL.pos, fontSize: 12 }}>✓</span>}
                      {status === "active" && <span style={{ width: 6, height: 6, borderRadius: 3, background: c }} />}
                    </div>
                    <div style={{ fontSize: 12.5, color: t.fg0, fontWeight: 500, lineHeight: 1.3 }}>{m.title || `Етап ${origNum}`}</div>
                    <div style={{ height: 3, background: t.bg3, borderRadius: 2, overflow: "hidden", marginTop: 4 }}>
                      <div style={{ width: `${pct}%`, height: "100%", background: c }} />
                    </div>
                    <span style={{ fontSize: 10, color: t.fg2, fontFamily: FONT_MONO }}>{done}/{total} · {pct}%</span>
                  </div>
                );
              })}
            </div>
          );

          const renderMonthList = (months) => months.map((m) => {
            const origIdx = workplan.indexOf(m);
            const c = m.color || PAL.brand2;
            const open = openMonths.has(origIdx);
            return (
              <div key={`wp-${origIdx}`} style={{ borderTop: `2px solid ${c}`, padding: "16px 20px", background: t.bg2 }}>
                <div onClick={() => toggleMonth(origIdx)} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: open ? 12 : 0, cursor: "pointer" }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: t.fg0 }}>Місяць {m.month || (origIdx + 1)} · {m.title}</span>
                  <span style={{ fontSize: 11, color: t.fg2 }}>{(m.tasks || []).filter(tk => tk.done || tk.status === "done").length} / {(m.tasks || []).length} виконано</span>
                  <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, color: c, padding: "5px 12px", borderRadius: 8, border: `1px solid ${c}40`, background: `${c}14`, whiteSpace: "nowrap" }}>
                    {open ? "Згорнути ▲" : `Розгорнути задачі (${(m.tasks || []).length}) ▼`}
                  </span>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {open && (m.tasks || []).map((task, ti) => {
                    const key = `${origIdx}-${ti}`;
                    const isOpen = !!wpTaskExp[key];
                    const isDone = task.done || task.status === "done";
                    const hasDetails = task.client || task.clientValue || task.manager || task.description || task.term || task.duration;
                    return (
                      <div key={ti} style={{
                        borderRadius: 8,
                        background: isDone ? `${PAL.pos}0d` : t.bg1,
                        border: `1px solid ${isDone ? `${PAL.pos}26` : t.line}`,
                        overflow: "hidden",
                        transition: "background .15s",
                      }}>
                        <div
                          onClick={hasDetails ? () => setWpTaskExp(s => ({ ...s, [key]: !s[key] })) : undefined}
                          style={{
                            display: "flex", alignItems: "center", gap: 12,
                            padding: "10px 12px", cursor: hasDetails ? "pointer" : "default",
                          }}
                        >
                          <div style={{
                            width: 20, height: 20, borderRadius: 6, flexShrink: 0,
                            background: isDone ? PAL.pos : "transparent",
                            border: isDone ? "none" : `2px solid ${t.line}`,
                            display: "flex", alignItems: "center", justifyContent: "center",
                          }}>
                            {isDone && <span style={{ color: "#fff", fontSize: 12, fontWeight: 700 }}>✓</span>}
                          </div>
                          {task.stage_no && (
                            <span style={{ fontSize: 10, fontWeight: 700, color: m.color || PAL.brand2, fontFamily: FONT_MONO, minWidth: 22, textAlign: "right" }}>
                              {task.stage_no}
                            </span>
                          )}
                          <span style={{ flex: 1, fontSize: 12.8, color: isDone ? t.fg2 : t.fg0, fontWeight: 500, textDecoration: isDone ? "line-through" : "none" }}>
                            {task.name || task.text || task.title || `Задача ${ti + 1}`}
                          </span>
                          {(task.term || task.duration) && (
                            <span style={{ fontSize: 10, color: t.fg2, fontFamily: FONT_MONO, padding: "2px 8px", background: t.bg2, borderRadius: 4, border: `1px solid ${t.line}` }}>
                              ⏱ {task.term || task.duration}
                            </span>
                          )}
                          {task.hours && <span style={{ fontSize: 10, color: t.fg3, fontFamily: FONT_MONO }}>{task.hours}год</span>}
                          {hasDetails && (
                            <span style={{ fontSize: 14, color: t.fg2, transition: "transform .2s", transform: isOpen ? "rotate(90deg)" : "rotate(0)", display: "inline-block" }}>›</span>
                          )}
                        </div>
                        {isOpen && (task.client || task.clientValue) && (
                          <div style={{
                            padding: "0 12px 14px 54px", fontSize: 12.5, color: t.fg1,
                            lineHeight: 1.55, borderTop: `1px dashed ${t.line}`, paddingTop: 12, marginTop: 0,
                          }}>
                            <div style={{ fontSize: 9.5, fontWeight: 700, color: PAL.pos, textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 5 }}>
                              Що ви отримаєте
                            </div>
                            {task.client || task.clientValue}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          });

          return (
            <section data-blk="workplan" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12 }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: t.fg0 }}>План робіт</h2>
                  <span style={{ fontSize: 12, color: t.fg2 }}>{workplan.length} етапів · {totalDone} / {totalTasks} задач виконано</span>
                </div>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <span style={{ fontSize: 28, fontWeight: 500, color: PAL.brand2, fontFamily: FONT_MONO }}>{totalPct}%</span>
                  <div style={{ width: 200, height: 8, background: t.bg3, borderRadius: 4, overflow: "hidden" }}>
                    <div style={{ width: `${totalPct}%`, height: "100%", background: `linear-gradient(90deg, ${PAL.brand} 0%, ${PAL.brand2} 100%)` }} />
                  </div>
                </div>
              </div>
              {pastPart.length > 0 && (
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: PAL.pos, letterSpacing: "0.14em" }}>✓ ЗРОБЛЕНО</span>
                    <div style={{ flex: 1, height: 1, background: `${PAL.pos}30` }} />
                  </div>
                  <div style={{ background: t.bg1, border: `1px solid ${t.line}`, borderRadius: 12, padding: 0, overflow: "hidden" }}>
                    {renderMonthGrid(pastPart)}
                    {renderMonthList(pastPart)}
                  </div>
                </div>
              )}
              {currPart.length > 0 && (
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: "#f59e0b", letterSpacing: "0.14em" }}>● ПОТОЧНИЙ МІСЯЦЬ · {mPct(currPart[0])}% виконано</span>
                    <div style={{ flex: 1, height: 1, background: `${PAL.brand2}30` }} />
                  </div>
                  <div style={{ background: t.bg1, border: `1px solid ${t.line}`, borderRadius: 12, padding: 0, overflow: "hidden" }}>
                    {renderMonthGrid(currPart)}
                    {renderMonthList(currPart)}
                  </div>
                </div>
              )}
              {futurePart.length > 0 && (
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: PAL.brand2, letterSpacing: "0.14em" }}>◆ ПЛАНИ (НАСТУПНИЙ МІСЯЦЬ)</span>
                    <div style={{ flex: 1, height: 1, background: `${PAL.brand2}30` }} />
                  </div>
                  <div style={{ background: t.bg1, border: `1px solid ${t.line}`, borderRadius: 12, padding: 0, overflow: "hidden" }}>
                    {renderMonthGrid(futurePart)}
                    {renderMonthList(futurePart)}
                  </div>
                </div>
              )}
              {aiFor("general") && <AINarrator t={t} {...aiFor("general")} />}
            </section>
          );
        })()}

        {/* ─── KANBAN (read-only) ─────────────────────────────── */}
        {kanban && kanban.columns?.length > 0 && (
          <section data-blk="kanban" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12 }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: t.fg0 }}>Задачі проєкту</h2>
                <span style={{ fontSize: 12, color: t.fg2 }}>Над чим працюємо та що вже здано{taskRange === "m1" ? " · за 30 днів" : taskRange === "m2" ? " · за 60 днів" : taskRange === "m3" ? " · за 90 днів" : " · за весь час"}</span>
              </div>
              <div style={{ display: "flex", gap: 4 }}>
                {[{ v: "m1", l: "30 днів" }, { v: "m2", l: "60 днів" }, { v: "m3", l: "90 днів" }, { v: "all", l: "Всі" }].map(o => (
                  <button key={o.v} onClick={() => setTaskRange(o.v)} style={{
                    fontSize: 10.5, fontFamily: FONT_MONO, padding: "3px 9px", borderRadius: 6, cursor: "pointer", whiteSpace: "nowrap",
                    border: `1px solid ${taskRange === o.v ? PAL.brand : t.line}`,
                    background: taskRange === o.v ? PAL.brand : "transparent",
                    color: taskRange === o.v ? "#fff" : t.fg2, fontWeight: taskRange === o.v ? 600 : 400,
                  }}>{o.l}</button>
                ))}
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(kanban.columns.length, 6)}, 1fr)`, gap: 10, overflowX: "auto" }}>
              {kanban.columns.map(col => {
                const colTasks = (kanban.tasks || []).filter(tk => tk.column_id === col.id);
                return (
                  <div key={col.id} style={{ background: t.bg2, border: `1px solid ${t.line}`, borderRadius: 12, padding: 12, minWidth: 180, display: "flex", flexDirection: "column", gap: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 10.5, fontWeight: 700, color: t.fg2, textTransform: "uppercase", letterSpacing: "0.1em" }}>{col.title}</span>
                      <span style={{ fontSize: 10, color: t.fg3, fontFamily: FONT_MONO, padding: "1px 6px", background: t.bg1, borderRadius: 4 }}>{colTasks.length}</span>
                    </div>
                    {colTasks.length === 0 && <div style={{ fontSize: 10.5, color: t.fg3, padding: "10px 0", textAlign: "center" }}>Порожньо</div>}
                    {colTasks.slice(0, 8).map(tk => {
                      const pmap = { high: PAL.neg, medium: PAL.warn, low: t.fg3 };
                      const dot = pmap[tk.priority] || t.fg3;
                      return (
                        <div key={tk.id} onClick={() => setTaskModal(tk)} title="Відкрити задачу"
                          style={{ background: t.bg1, border: `1px solid ${t.line}`, borderRadius: 8, padding: "8px 10px", display: "flex", flexDirection: "column", gap: 4, cursor: "pointer" }}>
                          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                            <span style={{ width: 5, height: 5, borderRadius: "50%", background: dot, flexShrink: 0 }} />
                            <span style={{ fontSize: 11.5, color: t.fg0, fontWeight: 500, flex: 1, lineHeight: 1.35 }}>{tk.title}</span>
                          </div>
                          {tk.qa_passed_at && <span style={{ fontSize: 9.5, color: PAL.pos, fontWeight: 600 }}>✓ QA</span>}
                        </div>
                      );
                    })}
                    {colTasks.length > 8 && (
                      <div style={{ fontSize: 10, color: t.fg3, textAlign: "center", paddingTop: 4 }}>+{colTasks.length - 8} ще</div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ─── МОДАЛКА ЗАДАЧІ (перегляд + PDF) ──────────────── */}
        {taskModal && (() => {
          const tk = taskModal;
          const RP_STATUS = { done: "Здано", in_progress: "В роботі", open: "Нова задача", take_to_work: "Взято в роботу", planned_3days: "Заплановано", in_work_today: "В роботі сьогодні", post_impl_review: "Перевірка після впровадження", qa_rejected: "На доопрацювання", closed: "Завершено", sem_take: "Семантика", sem_work: "Семантика в роботі" };
          const statusLabel = RP_STATUS[tk.status] || tk.status || "";
          const looksHtml = /<[a-z!/][\s\S]*>/i.test(tk.description || "") || /&[a-z#0-9]+;/i.test(tk.description || "");
          const pdf = () => {
            const w = window.open("", "_blank");
            if (!w) { alert("Дозвольте спливаючі вікна, щоб зберегти PDF"); return; }
            const esc = s => String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
            const body = looksHtml ? (tk.description || "") : `<div style="white-space:pre-wrap">${esc(tk.description || "Без опису")}</div>`;
            w.document.write(`<!DOCTYPE html><html lang="uk"><head><meta charset="utf-8"><title>${esc(tk.title)}</title><style>body{font-family:Inter,system-ui,-apple-system,sans-serif;color:#111827;max-width:720px;margin:40px auto;padding:0 26px;line-height:1.55}h1{font-size:22px;margin:0 0 8px}.meta{color:#6b7280;font-size:13px;margin-bottom:22px;border-bottom:1px solid #eee;padding-bottom:14px}.badge{display:inline-block;padding:3px 10px;border-radius:6px;background:#eef2ff;color:#4338ca;font-size:12px;font-weight:700;margin-right:8px}.body{font-size:14px}.body,.body *{color:#111827!important}.body a{color:#4338ca!important}img{max-width:100%;height:auto}table{border-collapse:collapse!important;table-layout:auto!important;width:auto!important;max-width:none}col,colgroup{width:auto!important}td,th{border:1px solid #cbd5e1;padding:5px 9px;white-space:normal;word-break:break-word}</style></head><body><h1>${esc(tk.title)}</h1><div class="meta"><span class="badge">${esc(statusLabel)}</span>${tk.updated_at ? "Оновлено: " + new Date(tk.updated_at).toLocaleDateString("uk-UA") : ""}</div><div class="body">${body}</div></body></html>`);
            w.document.close();
            setTimeout(() => { w.focus(); w.print(); }, 350);
          };
          return (
            <div onClick={() => setTaskModal(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
              <div onClick={e => e.stopPropagation()} style={{ background: "#fff", color: "#111827", borderRadius: 10, width: "100%", maxWidth: 820, maxHeight: "92vh", overflowY: "auto", overflowX: "hidden", padding: "clamp(22px,4vw,48px)", boxShadow: "0 24px 70px rgba(0,0,0,0.4)", overflowWrap: "break-word", wordBreak: "break-word" }}>
                <style>{`.rp-taskbody{overflow-x:auto;overflow-wrap:break-word;word-break:break-word}.rp-taskbody img{max-width:100%;height:auto}.rp-taskbody table{border-collapse:collapse!important;table-layout:auto!important;width:auto!important;max-width:none!important}.rp-taskbody col,.rp-taskbody colgroup{width:auto!important}.rp-taskbody td,.rp-taskbody th{border:1px solid #cbd5e1!important;padding:5px 9px;white-space:normal!important;word-break:break-word}.rp-taskbody pre{white-space:pre-wrap;word-break:break-word}.rp-taskbody,.rp-taskbody *{color:#1f2937!important}.rp-taskbody a{color:#4338ca!important}.rp-taskbody h1,.rp-taskbody h2,.rp-taskbody h3,.rp-taskbody b,.rp-taskbody strong{color:#111827!important}`}</style>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 10 }}>
                  <h1 style={{ fontSize: 24, fontWeight: 800, color: "#111827", margin: 0, lineHeight: 1.25 }}>{tk.title}</h1>
                  <button onClick={() => setTaskModal(null)} style={{ background: "none", border: "none", fontSize: 26, color: "#9ca3af", cursor: "pointer", lineHeight: 1 }}>×</button>
                </div>
                <div style={{ marginBottom: 20, paddingBottom: 16, borderBottom: "1px solid #eee", fontSize: 13, color: "#6b7280" }}>
                  <span style={{ padding: "3px 10px", borderRadius: 6, background: "#eef2ff", color: "#4338ca", fontWeight: 700, marginRight: 8 }}>{statusLabel}</span>
                  {tk.updated_at && <span>Оновлено: {new Date(tk.updated_at).toLocaleDateString("uk-UA")}</span>}
                </div>
                <div className="rp-taskbody" style={{ fontSize: 15, color: "#1f2937", lineHeight: 1.7 }}>
                  {looksHtml
                    ? <div dangerouslySetInnerHTML={{ __html: tk.description }} />
                    : <div style={{ whiteSpace: "pre-wrap" }}>{tk.description || "Без опису"}</div>}
                </div>
                {tk.spec_url && <div style={{ marginTop: 18 }}><a href={tk.spec_url} target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#4338ca", fontWeight: 700, fontSize: 14, textDecoration: "none" }}>📄 Відкрити повне ТЗ →</a></div>}
                <div style={{ marginTop: 26, display: "flex", gap: 10, justifyContent: "flex-end", position: "sticky", bottom: 0, background: "#fff", paddingTop: 14 }}>
                  <button onClick={() => setTaskModal(null)} style={{ background: "#f3f4f6", border: "1px solid #e5e7eb", color: "#374151", borderRadius: 9, padding: "10px 18px", fontSize: 14, cursor: "pointer" }}>Закрити</button>
                  <button onClick={pdf} style={{ background: "#4338ca", border: "none", color: "#fff", borderRadius: 9, padding: "10px 20px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>⬇ Завантажити PDF</button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ─── ТЕКСТИ НА ПОГОДЖЕННЯ ──────────────────────────── */}
        {seoTexts.length > 0 && (() => {
          const pending = seoTexts.filter(x => x.status === "pending_client");
          const reviewed = seoTexts.filter(x => x.status !== "pending_client");
          const statusChip = (s) => {
            const m = {
              pending_client: { k: "info", l: "Очікує вашого рішення" },
              approved: { k: "pos", l: "✅ Погоджено" },
              rejected: { k: "neg", l: "❌ На доопрацюванні" },
            }[s] || { k: "info", l: s };
            return <ClientChip kind={m.k} t={t}>{m.l}</ClientChip>;
          };
          const renderText = (txt) => {
            const isOpen = openTextId === txt.id;
            const path = (() => { try { return new URL(txt.url).pathname; } catch { return txt.url; } })();
            return (
              <div key={txt.id} style={{ background: t.bg1, border: `1px solid ${t.line}`, borderRadius: 12, overflow: "hidden" }}>
                <div onClick={() => setOpenTextId(isOpen ? null : txt.id)}
                  style={{ padding: "12px 16px", display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}>
                  <span style={{ fontSize: 11, color: PAL.brand, transform: isOpen ? "rotate(90deg)" : "none", transition: "transform .15s" }}>▶</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: t.fg0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{path}</div>
                    <a href={txt.url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ fontSize: 11, color: t.fg2, textDecoration: "none" }}>{txt.url} ↗</a>
                  </div>
                  {statusChip(txt.status)}
                </div>
                {isOpen && (
                  <div style={{ padding: "0 16px 16px" }}>
                    <div ref={isOpen ? previewRef : null} onMouseUp={() => captureSelection(txt.id)} style={{ background: "#fff", color: "#1a1a2e", borderRadius: 10, padding: "18px 22px", maxHeight: 460, overflow: "auto", fontSize: 14, lineHeight: 1.65, marginBottom: 14, userSelect: "text", cursor: "text" }}
                      dangerouslySetInnerHTML={{ __html: txt.html_content || "<p>(текст порожній)</p>" }} />
                    <div style={{ fontSize: 11, color: t.fg2, marginTop: -8, marginBottom: 12 }}>💡 Виділіть частину тексту, щоб залишити коментар саме до неї.</div>
                    {frag && frag.textId === txt.id && (
                      <div style={{ background: "rgba(99,102,241,0.08)", border: `1px solid ${PAL.brand}`, borderRadius: 10, padding: "12px 14px", marginBottom: 14 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: PAL.brand, marginBottom: 6 }}>💬 Коментар до виділеного фрагмента</div>
                        <div style={{ fontSize: 12.5, fontStyle: "italic", color: t.fg0, background: "rgba(99,102,241,0.10)", borderLeft: `3px solid ${PAL.brand}`, padding: "6px 10px", borderRadius: 6, marginBottom: 8, maxHeight: 80, overflow: "auto" }}>«{frag.quote}»</div>
                        <textarea value={fragText} onChange={e => setFragText(e.target.value)} rows={2} placeholder="Що змінити саме тут?"
                          style={{ width: "100%", boxSizing: "border-box", background: t.bg2, border: `1px solid ${t.line}`, borderRadius: 8, padding: "9px 11px", color: t.fg0, fontSize: 12.5, fontFamily: "inherit", outline: "none", resize: "vertical", marginBottom: 8 }} />
                        <div style={{ display: "flex", gap: 8 }}>
                          <button onClick={() => submitFragmentComment(txt.id)} disabled={fragBusy || !fragText.trim()}
                            style={{ background: PAL.brand, color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 12.5, fontWeight: 600, cursor: (fragBusy || !fragText.trim()) ? "not-allowed" : "pointer", opacity: (fragBusy || !fragText.trim()) ? 0.5 : 1, fontFamily: "inherit" }}>
                            {fragBusy ? "..." : "Надіслати коментар"}
                          </button>
                          <button onClick={() => { setFrag(null); setFragText(""); }}
                            style={{ background: "transparent", color: t.fg2, border: `1px solid ${t.line}`, borderRadius: 8, padding: "8px 16px", fontSize: 12.5, cursor: "pointer", fontFamily: "inherit" }}>Скасувати</button>
                        </div>
                      </div>
                    )}

                    {/* Conversation thread */}
                    {Array.isArray(txt.comments) && txt.comments.length > 0 && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
                        {txt.comments.map(c => {
                          const isClient = c.author_type === "client";
                          return (
                            <div key={c.id} style={{
                              alignSelf: isClient ? "flex-end" : "flex-start", maxWidth: "85%",
                              background: isClient ? "rgba(99,102,241,0.12)" : t.bg2,
                              border: `1px solid ${isClient ? "rgba(99,102,241,0.25)" : t.line}`,
                              borderRadius: 10, padding: "8px 12px",
                            }}>
                              <div style={{ fontSize: 10, color: isClient ? PAL.brand : t.fg2, fontWeight: 700, marginBottom: 3 }}>
                                {isClient ? "Ви" : `Менеджер${c.author_name ? " · " + c.author_name : ""}`}
                                <span style={{ fontWeight: 400, marginLeft: 6, opacity: 0.7 }}>{new Date(c.created_at).toLocaleString("uk-UA", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
                              </div>
                              {c.anchor && c.anchor.quote && (
                                <div style={{ fontSize: 11.5, fontStyle: "italic", color: t.fg2, borderLeft: `3px solid ${PAL.brand}`, paddingLeft: 8, marginBottom: 4 }}>«{c.anchor.quote}»</div>
                              )}
                              <div style={{ fontSize: 13, color: t.fg0, whiteSpace: "pre-wrap", lineHeight: 1.45 }}>{c.body}</div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <textarea
                      value={textComments[txt.id] ?? ""}
                      onChange={e => setTextComments(prev => ({ ...prev, [txt.id]: e.target.value }))}
                      placeholder="Ваш коментар або відповідь (обов'язково при відхиленні)"
                      rows={3}
                      style={{ width: "100%", boxSizing: "border-box", background: t.bg2, border: `1px solid ${t.line}`, borderRadius: 8, padding: "10px 12px", color: t.fg0, fontSize: 12.5, fontFamily: "inherit", outline: "none", resize: "vertical", marginBottom: 12 }}
                    />
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                      <button onClick={() => submitTextReview(txt.id, "approved")} disabled={textBusyId === txt.id}
                        style={{ background: PAL.pos, color: "#fff", border: "none", borderRadius: 8, padding: "10px 20px", fontSize: 13, fontWeight: 600, cursor: textBusyId === txt.id ? "wait" : "pointer", fontFamily: "inherit" }}>
                        {textBusyId === txt.id ? "..." : "✅ Погодити"}
                      </button>
                      <button onClick={() => submitTextReview(txt.id, "rejected")} disabled={textBusyId === txt.id}
                        style={{ background: "transparent", color: PAL.neg, border: `1px solid ${PAL.neg}`, borderRadius: 8, padding: "10px 20px", fontSize: 13, fontWeight: 600, cursor: textBusyId === txt.id ? "wait" : "pointer", fontFamily: "inherit" }}>
                        ❌ Не погоджено — переписати
                      </button>
                      <button onClick={() => submitThreadComment(txt.id)} disabled={threadBusyId === txt.id || !(textComments[txt.id] || "").trim()}
                        style={{ background: "transparent", color: PAL.brand, border: `1px solid ${PAL.brand}`, borderRadius: 8, padding: "10px 20px", fontSize: 13, fontWeight: 600, cursor: (threadBusyId === txt.id || !(textComments[txt.id] || "").trim()) ? "not-allowed" : "pointer", opacity: (threadBusyId === txt.id || !(textComments[txt.id] || "").trim()) ? 0.5 : 1, fontFamily: "inherit" }}>
                        {threadBusyId === txt.id ? "..." : "💬 Лишити коментар"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          };
          return (
            <section data-blk="seoTexts" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: t.fg0 }}>Тексти на погодження{pending.length ? ` · ${pending.length}` : ""}</h2>
                <span style={{ fontSize: 12, color: t.fg2 }}>Перегляньте підготовлені тексти. Погодьте або відправте на доопрацювання з коментарем.</span>
              </div>
              {pending.length === 0 && reviewed.length > 0 && (
                <div style={{ fontSize: 13, color: PAL.pos }}>Усі поточні тексти опрацьовано ✔</div>
              )}
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {pending.map(renderText)}
              </div>
              {reviewed.length > 0 && (
                <>
                  <div style={{ fontSize: 11, color: t.fg3, textTransform: "uppercase", letterSpacing: "0.08em", marginTop: 6 }}>Опрацьовані</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {reviewed.map(renderText)}
                  </div>
                </>
              )}
            </section>
          );
        })()}

        {/* ─── ЗАПИТИ ТА ЗАУВАЖЕННЯ (CLIENT TICKETS) ──────────── */}
        <section data-blk="tickets" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12 }}>
            <div>
              <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: t.fg0 }}>Запити та зауваження</h2>
              <span style={{ fontSize: 12, color: t.fg2 }}>Усе, що ви запитували — фіксуємо тут{tickets.length > 0 ? ` · ${tickets.length}` : ""}</span>
            </div>
            <button
              onClick={() => setNewTicket(s => ({ ...s, open: !s.open }))}
              style={{ background: newTicket.open ? t.bg2 : `linear-gradient(135deg, ${PAL.brand} 0%, ${PAL.brand2} 100%)`,
                color: newTicket.open ? t.fg0 : "#fff",
                border: `1px solid ${newTicket.open ? t.line : "transparent"}`,
                borderRadius: 8, padding: "8px 16px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
              {newTicket.open ? "Скасувати" : "+ Новий запит"}
            </button>
          </div>

          {newTicket.open && (
            <div style={{ background: t.bg1, border: `1px solid ${t.line}`, borderRadius: 12, padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
              <input
                type="text"
                placeholder="Коротко: що сталося / що потрібно зробити"
                value={newTicket.title}
                onChange={(e) => setNewTicket(s => ({ ...s, title: e.target.value }))}
                style={{ background: t.bg2, border: `1px solid ${t.line}`, borderRadius: 8, padding: "10px 12px", color: t.fg0, fontSize: 13, fontFamily: "inherit", outline: "none" }}
              />
              <textarea
                placeholder="Деталі (опціонально): URL сторінки, що очікуєте побачити, скріни тощо"
                value={newTicket.description}
                onChange={(e) => setNewTicket(s => ({ ...s, description: e.target.value }))}
                rows={4}
                style={{ background: t.bg2, border: `1px solid ${t.line}`, borderRadius: 8, padding: "10px 12px", color: t.fg0, fontSize: 12.5, fontFamily: "inherit", outline: "none", resize: "vertical" }}
              />
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button onClick={() => setNewTicket({ title: "", description: "", open: false, sending: false })}
                  style={{ background: t.bg2, border: `1px solid ${t.line}`, color: t.fg0, borderRadius: 8, padding: "8px 14px", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>Скасувати</button>
                <button onClick={submitClientTicket} disabled={!newTicket.title.trim() || newTicket.sending}
                  style={{ background: `linear-gradient(135deg, ${PAL.brand} 0%, ${PAL.brand2} 100%)`, color: "#fff", border: "none",
                    borderRadius: 8, padding: "8px 16px", fontSize: 12.5, fontWeight: 600, cursor: newTicket.title.trim() ? "pointer" : "not-allowed", opacity: newTicket.title.trim() ? 1 : 0.5, fontFamily: "inherit" }}>
                  {newTicket.sending ? "Надсилаємо..." : "Надіслати"}
                </button>
              </div>
            </div>
          )}

          {tickets.length === 0 && !newTicket.open && (
            <div style={{ background: t.bg1, border: `1px dashed ${t.line}`, borderRadius: 12, padding: "30px 20px", textAlign: "center", color: t.fg2, fontSize: 13 }}>
              Поки що немає жодних запитів. Натисніть «+ Новий запит» якщо щось помітили або хочете уточнити.
            </div>
          )}

          {tickets.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 10 }}>
              {tickets.map(tk => {
                const sm = {
                  open: { c: PAL.warn, l: "Відкрито", k: "warn" },
                  in_progress: { c: PAL.info, l: "В роботі", k: "info" },
                  in_review: { c: PAL.info, l: "На перевірці", k: "info" },
                  done: { c: PAL.pos, l: "Виконано", k: "pos" },
                  closed: { c: t.fg3, l: "Закрито", k: "neg" },
                }[tk.status] || { c: t.fg3, l: tk.status, k: "neg" };
                const pm = { high: PAL.neg, medium: PAL.warn, low: t.fg3 }[tk.priority] || t.fg3;
                const days = tk.created_at ? Math.floor((Date.now() - new Date(tk.created_at).getTime()) / 86400000) : 0;
                const cc = tk.comment_count || 0;
                return (
                  <div key={tk.id} style={{ background: t.bg1, border: `1px solid ${t.line}`, borderRadius: 12, padding: 14, display: "flex", flexDirection: "column", gap: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 11, color: t.fg3, fontFamily: FONT_MONO }}>#{tk.id}</span>
                      <ClientChip kind={sm.k} t={t}>{sm.l}</ClientChip>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 500, color: t.fg0, lineHeight: 1.4 }}>{tk.title}</div>
                    {tk.description && (
                      <div style={{ fontSize: 12, color: t.fg2, lineHeight: 1.5, whiteSpace: "pre-wrap", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{tk.description}</div>
                    )}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4 }}>
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: pm }} />
                        <span style={{ fontSize: 10.5, color: t.fg2, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                          {tk.priority === "high" ? "висока" : tk.priority === "low" ? "низька" : "середня"} пріор.
                        </span>
                      </div>
                      <span style={{ fontSize: 10.5, color: t.fg3, fontFamily: FONT_MONO }}>
                        {days === 0 ? "сьогодні" : days === 1 ? "вчора" : `${days} дн. тому`}
                      </span>
                    </div>
                    <div style={{ marginTop: 4, display: "flex", gap: 6 }}>
                      <button onClick={() => openTicketModal(tk.id)}
                        style={{ flex: 1, background: t.bg2, color: PAL.brand, border: `1px solid ${PAL.brand}44`, borderRadius: 8, padding: "8px 12px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                        💬 Відкрити запит {cc > 0 ? `· ${cc} відп.` : ""}
                      </button>
                      {tk.is_client && (
                        <button onClick={() => deleteTicket(tk.id)} title="Видалити свій запит"
                          style={{ background: t.bg2, color: PAL.neg, border: `1px solid ${PAL.neg}44`, borderRadius: 8, padding: "8px 12px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                          🗑
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* ─── TICKET MODAL ──────────────────────────────────── */}
        {openTicketId != null && (() => {
          const tk = tickets.find(x => x.id === openTicketId);
          if (!tk) return null;
          const thread = ticketComments[tk.id] || [];
          const sm = {
            open: { l: "Відкрито", k: "warn" },
            in_progress: { l: "В роботі", k: "info" },
            in_review: { l: "На перевірці", k: "info" },
            done: { l: "Виконано", k: "pos" },
            closed: { l: "Закрито", k: "neg" },
          }[tk.status] || { l: tk.status, k: "neg" };
          return (
            <div onClick={closeTicketModal} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 9998, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
              <div onClick={e => e.stopPropagation()} style={{ background: t.bg0, color: t.fg0, borderRadius: 14, maxWidth: 640, width: "100%", maxHeight: "88vh", display: "flex", flexDirection: "column", border: `1px solid ${t.line}`, boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }}>
                {/* Header */}
                <div style={{ padding: "16px 20px", borderBottom: `1px solid ${t.line}`, display: "flex", alignItems: "flex-start", gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                      <span style={{ fontSize: 11, color: t.fg3, fontFamily: FONT_MONO }}>#{tk.id}</span>
                      <ClientChip kind={sm.k} t={t}>{sm.l}</ClientChip>
                    </div>
                    <div style={{ fontSize: 17, fontWeight: 700, color: t.fg0, lineHeight: 1.3 }}>{tk.title}</div>
                  </div>
                  <button onClick={closeTicketModal} style={{ background: t.bg2, border: `1px solid ${t.line}`, color: t.fg0, borderRadius: 8, padding: "4px 10px", fontSize: 16, lineHeight: 1, cursor: "pointer" }}>✕</button>
                </div>
                {/* Body — description + thread */}
                <div style={{ padding: "16px 20px", overflow: "auto", flex: 1 }}>
                  {tk.description && (
                    <div style={{ fontSize: 13, color: t.fg2, lineHeight: 1.6, whiteSpace: "pre-wrap", marginBottom: 16, paddingBottom: 16, borderBottom: `1px solid ${t.line}` }}>{tk.description}</div>
                  )}
                  <div style={{ fontSize: 11, color: t.fg3, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10 }}>
                    Листування{thread.length ? ` · ${thread.length}` : ""}
                  </div>
                  {thread.length === 0 ? (
                    <div style={{ fontSize: 12.5, color: t.fg2, fontStyle: "italic" }}>Поки що відповідей немає. Напишіть повідомлення — менеджер відповість тут.</div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {thread.map(c => {
                        const isClient = c.author_type === "client";
                        return (
                          <div key={c.id} style={{
                            alignSelf: isClient ? "flex-end" : "flex-start", maxWidth: "85%",
                            background: isClient ? "rgba(99,102,241,0.12)" : t.bg2,
                            border: `1px solid ${isClient ? "rgba(99,102,241,0.25)" : t.line}`,
                            borderRadius: 10, padding: "8px 12px",
                          }}>
                            <div style={{ fontSize: 10, color: isClient ? PAL.brand : t.fg2, fontWeight: 700, marginBottom: 3 }}>
                              {isClient ? "Ви" : `Менеджер · ${c.author_name}`}
                              <span style={{ fontWeight: 400, marginLeft: 6, opacity: 0.7 }}>{new Date(c.created_at).toLocaleString("uk-UA", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
                            </div>
                            <div style={{ fontSize: 13, color: t.fg0, whiteSpace: "pre-wrap", lineHeight: 1.45 }}>{c.text}</div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                {/* Footer — reply box */}
                <div style={{ padding: "12px 20px", borderTop: `1px solid ${t.line}`, display: "flex", gap: 8 }}>
                  <textarea
                    value={ticketReplyDraft[tk.id] || ""}
                    onChange={e => setTicketReplyDraft(prev => ({ ...prev, [tk.id]: e.target.value }))}
                    placeholder="Ваша відповідь..."
                    rows={2}
                    style={{ flex: 1, boxSizing: "border-box", background: t.bg2, border: `1px solid ${t.line}`, borderRadius: 8, padding: "8px 10px", color: t.fg0, fontSize: 12.5, fontFamily: "inherit", resize: "vertical", outline: "none" }}
                  />
                  <button onClick={() => submitTicketComment(tk.id)} disabled={ticketReplyBusy === tk.id || !(ticketReplyDraft[tk.id] || "").trim()}
                    style={{ alignSelf: "flex-end", background: `linear-gradient(135deg, ${PAL.brand} 0%, ${PAL.brand2} 100%)`, color: "#fff", border: "none", borderRadius: 8, padding: "9px 18px", fontSize: 12.5, fontWeight: 600, cursor: (ticketReplyBusy === tk.id || !(ticketReplyDraft[tk.id] || "").trim()) ? "not-allowed" : "pointer", opacity: (ticketReplyBusy === tk.id || !(ticketReplyDraft[tk.id] || "").trim()) ? 0.5 : 1, fontFamily: "inherit" }}>
                    {ticketReplyBusy === tk.id ? "..." : "Надіслати"}
                  </button>
                </div>
                {tk.is_client && (
                  <div style={{ padding: "0 20px 14px", display: "flex", justifyContent: "flex-end" }}>
                    <button onClick={() => deleteTicket(tk.id)}
                      style={{ background: "transparent", color: PAL.neg, border: `1px solid ${PAL.neg}44`, borderRadius: 8, padding: "6px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                      🗑 Видалити запит
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* ─── KPI 6 ─────────────────────────────────────────── */}
        <section data-blk="kpi" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: t.fg0 }}>Ключові показники</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
            <ClientKPI label="Кліки (30д)" value={last30Clicks} delta={clicksDelta} tone="pos" spark={sparkClicks} t={t} dark={rpDark}
              hint="Користувачі переходять на сайт із Google" />
            <ClientKPI label="Покази (30д)" value={last30Imp} delta={imprDelta} tone="brand" spark={sparkImp} t={t} dark={rpDark}
              hint="Скільки разів сайт показався в пошуку" />
            <ClientKPI label="TOP-10 запитів" value={top10Now} delta={top10Delta} tone={top10Delta != null && top10Delta < 0 ? "neg" : "pos"} spark={sparkTop10} t={t} dark={rpDark}
              hint="Запити на 1-10 місці — звідси приходить 95% трафіку" />
            <ClientKPI label="TOP-3 запитів" value={top3Now} delta={deltaPct(top3Now, top3Old)} tone="pos" spark={sparkTop3} t={t} dark={rpDark}
              hint="Найцінніші позиції — топ виборки Google" />
            <ClientKPI label="Реф. домени" value={refLatest || 0} delta={refDelta} tone="info" spark={sparkRef} t={t} dark={rpDark}
              hint="Сайти, які посилаються на вас. Більше = вищий авторитет" />
            <ClientKPI label="Всього запитів" value={totalQ} delta={queriesDelta} tone="brand" spark={sparkQ} t={t} dark={rpDark}
              hint="Усі ключові слова, по яких сайт ранжується" />
          </div>
          {aiFor("kpi") && <AINarrator t={t} {...aiFor("kpi")} />}
        </section>

        {/* ─── CWV ───────────────────────────────────────────── */}
        {ps && (
          <section data-blk="cwv" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: t.fg0 }}>Технічний аудит · Core Web Vitals</h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
              {[
                { k: "LCP", v: ps.lcp ? (ps.lcp/1000).toFixed(1) : "—", u: "s", thresh: ps.lcp <= 2500, hint: "Найбільший елемент завантажується" },
                { k: "CLS", v: ps.cls != null ? Number(ps.cls).toFixed(2) : "—", u: "", thresh: ps.cls <= 0.1, hint: "Стабільність layout" },
                { k: "FCP", v: ps.fcp ? (ps.fcp/1000).toFixed(1) : "—", u: "s", thresh: ps.fcp <= 1800, hint: "Перший контент з'являється" },
                { k: "TBT", v: ps.tbt != null ? Math.round(ps.tbt) : "—", u: "ms", thresh: ps.tbt <= 200, hint: "Реакція сторінки на дії" },
              ].map((m, i) => {
                const c = m.thresh ? PAL.pos : PAL.warn;
                return (
                  <div key={i} style={{ background: t.bg1, border: `1px solid ${t.line}`, borderRadius: 12, padding: 16, position: "relative", overflow: "hidden" }}>
                    <div style={{ position: "absolute", top: 0, right: 0, width: 60, height: 60, background: `radial-gradient(circle, ${c}20 0%, transparent 70%)` }} />
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                      <span style={{ fontSize: 10, fontWeight: 700, color: t.fg2, textTransform: "uppercase", letterSpacing: "0.12em" }}>{m.k}</span>
                      <ClientChip kind={m.thresh ? "pos" : "warn"} t={t}>{m.thresh ? "GOOD" : "WORK"}</ClientChip>
                    </div>
                    <div style={{ fontSize: 28, fontWeight: 500, color: t.fg0, fontFamily: FONT_MONO, letterSpacing: "-0.02em" }}>
                      {m.v}<span style={{ fontSize: 14, color: t.fg2, marginLeft: 4 }}>{m.u}</span>
                    </div>
                    <div style={{ fontSize: 11, color: t.fg2, marginTop: 8, paddingTop: 8, borderTop: `1px dashed ${t.line}`, lineHeight: 1.4 }}>{m.hint}</div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ─── BACKLINKS ─────────────────────────────────────── */}
        {bl && (
          <section data-blk="backlinks" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div>
              <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: t.fg0 }}>Посилальний профіль</h2>
              <span style={{ fontSize: 12, color: t.fg2 }}>
                {blSource === "openseo" ? "Дані OpenSEO · хто посилається на ваш сайт" : "Дані Ahrefs · хто посилається на ваш сайт"}
                {blSource === "openseo" && bl.fetchedAt ? ` · оновлено ${fmtDate(bl.fetchedAt)}` : ""}
              </span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: `repeat(${blSource === "openseo" ? 5 : 3}, 1fr)`, gap: 12 }}>
              {[
                { l: "Усього посилань", v: bl.total,      d: bl.totalDelta,      c: PAL.brand2, h: "Загальна кількість зовнішніх лінків" },
                { l: "Реф. домени",     v: bl.refDomains, d: bl.refDomainsDelta, c: PAL.info,   h: "Унікальних сайтів-донорів" },
                { l: "Domain Rank",     v: bl.dr,         d: bl.drDelta,         c: PAL.warn,   h: "Сила домену 0-100",          suffix: "/100" },
                ...(blSource === "openseo" ? [
                  { l: "Биті лінки", v: bl.brokenBacklinks ?? 0, d: 0, c: PAL.neg, h: "Посилання, що ведуть у нікуди" },
                  { l: "Spam score", v: bl.spamScore ?? 0,       d: 0, c: PAL.warn, h: "Ризиковість донор-сайтів 0-100", suffix: "/100" },
                ] : []),
              ].map((m, i) => {
                const showDelta = m.d != null && m.d !== 0;
                const dPos = (m.d || 0) >= 0;
                return (
                  <div key={i} style={{ background: t.bg1, border: `1px solid ${t.line}`, borderRadius: 12, padding: 14, display: "flex", flexDirection: "column", gap: 6, position: "relative", overflow: "hidden" }}>
                    <div style={{ position: "absolute", top: 0, left: 0, width: "100%", height: 2, background: m.c, opacity: 0.6 }} />
                    <span style={{ fontSize: 10, fontWeight: 700, color: t.fg2, textTransform: "uppercase", letterSpacing: "0.12em" }}>{m.l}</span>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                      <div style={{ fontSize: 26, fontWeight: 500, color: m.c, letterSpacing: "-0.02em", fontFamily: FONT_MONO }}>
                        {m.prefix || ""}{fmtNum(m.v)}{m.suffix || ""}
                      </div>
                      {showDelta && (
                        <span style={{ fontSize: 11, fontWeight: 700, color: dPos ? PAL.pos : PAL.neg, fontFamily: FONT_MONO }}>
                          {dPos ? "+" : ""}{fmtNum(m.d)}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 10.5, color: t.fg3, lineHeight: 1.35 }}>{m.h}</div>
                  </div>
                );
              })}
            </div>
            {aiFor("backlinks") && <AINarrator t={t} {...aiFor("backlinks")} />}
          </section>
        )}

        {/* ─── VALUE (money language) ─────────────────────────── */}
        {showValueBlk && (
          <section data-blk="value" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div>
              <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: t.fg0 }}>Заявки з органічного пошуку</h2>
              <span style={{ fontSize: 12, color: t.fg2 }}>Цільові дії, які органіка Google принесла на сайт</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14 }}>
              {_orgConv != null && !_convSuspect && _orgConv > 0 && (
                <ClientKPI label="Заявки з органіки" value={_orgConv} delta={_convDelta} tone="pos" hint="Цільові дії з органічного пошуку (GA4)" t={t} dark={rpDark} />
              )}
            </div>
          </section>
        )}

        {/* ─── GA4 ANALYTICS ─────────────────────────────────── */}
        {ga4 && (
          <section data-blk="ga4" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div>
              <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: t.fg0 }}>Поведінка користувачів</h2>
              <span style={{ fontSize: 12, color: t.fg2 }}>Дані Google Analytics 4 · {ga4Days} днів{ga4Sliced ? "" : (ga4.snapshotDate ? ` · оновлено ${fmtDate(ga4.snapshotDate)}` : "")}{periodWin > _ga4ts.length && _ga4ts.length ? ` · макс. наявних ${_ga4ts.length}` : ""}</span>
            </div>

            {/* GA4 KPI cards */}
            {(() => {
              const cur = ga4.cur || {}; const prev = ga4.prev || {};
              const pct = (c, p) => p > 0 ? Math.round(((c - p) / p) * 100) : null;
              // При зрізі періоду — Сесії/Користувачі/Перегляди рахуємо з обрізаного timeseries (реальні поденні).
              // Дельту при зрізі ховаємо (порівняння з попереднім періодом доступне лише для повного знімку).
              const items = [
                { l: "Сесії", v: ga4Sliced ? ga4Sum("sessions") : cur.sessions, d: ga4Sliced ? null : pct(cur.sessions, prev.sessions), c: PAL.brand, h: "Скільки разів користувачі заходили на сайт" },
                { l: "Користувачі", v: ga4Sliced ? ga4Sum("users") : cur.users, d: ga4Sliced ? null : pct(cur.users, prev.users), c: PAL.brand2, h: "Унікальних відвідувачів" },
                { l: "Нові", v: cur.newUsers, d: ga4Sliced ? null : pct(cur.newUsers, prev.newUsers), c: PAL.info, h: "Зайшли вперше", full: true },
                { l: "Залучені сесії", v: cur.engagedSessions, d: ga4Sliced ? null : pct(cur.engagedSessions, prev.engagedSessions), c: PAL.pos, h: "Сесії довше 10 сек з взаємодією", full: true },
                { l: "Перегляди", v: ga4Sliced ? ga4Sum("pageviews") : cur.pageViews, d: ga4Sliced ? null : pct(cur.pageViews, prev.pageViews), c: "#0ea5e9", h: "Скільки сторінок переглянули" },
                { l: "Конверсії", v: cur.conversions, d: ga4Sliced ? null : pct(cur.conversions, prev.conversions), c: PAL.warn, h: "Цільові події (заявки/покупки)", full: true },
              ];
              return (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12 }}>
                  {items.map((m, i) => (
                    <div key={i} style={{ background: t.bg1, border: `1px solid ${t.line}`, borderRadius: 12, padding: 14, display: "flex", flexDirection: "column", gap: 6, position: "relative", overflow: "hidden" }}>
                      <div style={{ position: "absolute", top: 0, left: 0, width: "100%", height: 2, background: m.c, opacity: 0.6 }} />
                      <span style={{ fontSize: 10, fontWeight: 700, color: t.fg2, textTransform: "uppercase", letterSpacing: "0.12em" }}>{m.l}{m.full && ga4Sliced ? <span style={{ color: t.fg3, fontWeight: 400 }}> · за 90д</span> : ""}</span>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                        <div style={{ fontSize: 24, fontWeight: 500, color: m.c, letterSpacing: "-0.02em", fontFamily: FONT_MONO }}>{fmtNum(m.v || 0)}</div>
                        {m.d != null && (
                          <ClientChip kind={m.d >= 0 ? "pos" : "neg"} t={t}>
                            {m.d >= 0 ? "▲" : "▼"} {Math.abs(m.d)}%
                          </ClientChip>
                        )}
                      </div>
                      <div style={{ fontSize: 10.5, color: t.fg3, lineHeight: 1.35 }}>{m.h}</div>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* Daily timeseries chart */}
            {ga4.timeseries?.length > 0 && (
              <div style={{ background: t.bg1, border: `1px solid ${t.line}`, borderRadius: 12, padding: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: t.fg0 }}>Динаміка сесій та користувачів</span>
                  <div style={{ display: "flex", gap: 14 }}>
                    <span style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 11, color: t.fg2 }}><span style={{ width: 8, height: 8, borderRadius: 4, background: PAL.brand }} />Сесії</span>
                    <span style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 11, color: t.fg2 }}><span style={{ width: 8, height: 8, borderRadius: 4, background: PAL.brand2 }} />Користувачі</span>
                  </div>
                </div>
                <div style={{ height: 240 }}>
                  <Line
                    data={{
                      labels: ga4slice.map(d => { const s = d.date || ""; return s ? `${s.slice(6, 8)}.${s.slice(4, 6)}` : ""; }),
                      datasets: [
                        { label: "Сесії", data: ga4slice.map(d => d.sessions || 0), borderColor: PAL.brand, backgroundColor: hexA(PAL.brand, 0.14), fill: true, tension: 0.4, pointRadius: 0, borderWidth: 2 },
                        { label: "Користувачі", data: ga4slice.map(d => d.users || 0), borderColor: PAL.brand2, backgroundColor: hexA(PAL.brand2, 0.08), fill: true, tension: 0.4, pointRadius: 0, borderWidth: 2 },
                      ],
                    }}
                    options={chartOpts(t, rpDark)}
                  />
                </div>
              </div>
            )}

            {/* Two-col: Sources + Devices */}
            <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 14 }}>
              {ga4.sources?.length > 0 && (
                <div style={{ background: t.bg1, border: `1px solid ${t.line}`, borderRadius: 12, padding: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: t.fg0, marginBottom: 10 }}>Джерела трафіку{ga4Sliced && <span style={{ fontSize: 11, fontWeight: 400, color: t.fg3 }}> · за {ga4FullDays}д</span>}</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {ga4.sources.slice(0, 8).map((s, i) => {
                      const max = Math.max(...ga4.sources.map(x => x.sessions || 0), 1);
                      const pctBar = Math.round(((s.sessions || 0) / max) * 100);
                      return (
                        <div key={i}>
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                            <span style={{ color: t.fg0, fontWeight: 500 }}>{s.channel || "—"}</span>
                            <span style={{ color: t.fg2, fontFamily: FONT_MONO }}>
                              {fmtNum(s.sessions)}
                              {s.conversions > 0 && <span style={{ color: PAL.pos, marginLeft: 8 }}>· {fmtNum(s.conversions)} конв.</span>}
                            </span>
                          </div>
                          <div style={{ height: 6, background: t.bg3, borderRadius: 3, overflow: "hidden" }}>
                            <div style={{ height: "100%", width: `${pctBar}%`, background: PAL.brand, transition: "width .4s" }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {ga4.devices?.length > 0 && (
                <div style={{ background: t.bg1, border: `1px solid ${t.line}`, borderRadius: 12, padding: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: t.fg0, marginBottom: 10 }}>Пристрої{ga4Sliced && <span style={{ fontSize: 11, fontWeight: 400, color: t.fg3 }}> · за {ga4FullDays}д</span>}</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {(() => {
                      const total = ga4.devices.reduce((s, d) => s + (d.sessions || 0), 0) || 1;
                      const labels = { mobile: "Мобільні", desktop: "Десктоп", tablet: "Планшети" };
                      const colors = { mobile: PAL.brand, desktop: PAL.brand2, tablet: PAL.info };
                      return ga4.devices.map((d, i) => {
                        const pct = Math.round(((d.sessions || 0) / total) * 100);
                        const lbl = labels[d.device] || d.device;
                        const c = colors[d.device] || t.fg2;
                        return (
                          <div key={i}>
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 5 }}>
                              <span style={{ color: t.fg0, fontWeight: 500 }}>{lbl}</span>
                              <span style={{ color: t.fg2, fontFamily: FONT_MONO }}>{fmtNum(d.sessions)} · {pct}%</span>
                            </div>
                            <div style={{ height: 8, background: t.bg3, borderRadius: 4, overflow: "hidden" }}>
                              <div style={{ height: "100%", width: `${pct}%`, background: c, transition: "width .4s" }} />
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              )}
            </div>

            {/* Top landing pages */}
            {ga4.landingPages?.length > 0 && (
              <div style={{ background: t.bg1, border: `1px solid ${t.line}`, borderRadius: 12, padding: 0, overflow: "hidden" }}>
                <div style={{ padding: "12px 16px", borderBottom: `1px solid ${t.line}`, fontSize: 13, fontWeight: 600, color: t.fg0 }}>
                  Топ сторінки входу{ga4Sliced && <span style={{ fontSize: 11, fontWeight: 400, color: t.fg3 }}> · за {ga4FullDays}д</span>}
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: t.bg2, color: t.fg2 }}>
                      <th style={{ padding: "9px 16px", textAlign: "left", fontSize: 10.5, letterSpacing: "0.1em", fontWeight: 600 }}>СТОРІНКА</th>
                      <th style={{ padding: "9px 16px", textAlign: "right", fontSize: 10.5, letterSpacing: "0.1em", fontWeight: 600 }}>СЕСІЇ</th>
                      <th style={{ padding: "9px 16px", textAlign: "right", fontSize: 10.5, letterSpacing: "0.1em", fontWeight: 600 }}>КОНВЕРСІЇ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ga4.landingPages.slice(0, 10).map((p, i) => (
                      <tr key={i} style={{ borderTop: `1px solid ${t.line}` }}>
                        <td style={{ padding: "9px 16px", color: t.fg0, fontFamily: FONT_MONO, fontSize: 11, maxWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={p.page}>{p.page}</td>
                        <td style={{ padding: "9px 16px", textAlign: "right", color: t.fg0, fontFamily: FONT_MONO, fontWeight: 500 }}>{fmtNum(p.sessions)}</td>
                        <td style={{ padding: "9px 16px", textAlign: "right", color: p.conversions > 0 ? PAL.pos : t.fg3, fontFamily: FONT_MONO, fontWeight: 500 }}>{p.conversions > 0 ? fmtNum(p.conversions) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {aiFor && aiFor("ga4") && <AINarrator t={t} {...aiFor("ga4")} />}
          </section>
        )}

        {/* ─── GSC TRAFFIC CHART ─────────────────────────────── */}
        {dailyArr.length > 0 && (
          <section data-blk="gscTraffic" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12 }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: t.fg0 }}>Динаміка кліків та показів</h2>
                <span style={{ fontSize: 12, color: t.fg2 }}>Дані Google Search Console · {dailyArr.length} днів</span>
              </div>
              <div style={{ display: "flex", gap: 14 }}>
                <span style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 11, color: t.fg2 }}><span style={{ width: 8, height: 8, borderRadius: 4, background: PAL.brand2 }} />Кліки</span>
                <span style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 11, color: t.fg2 }}><span style={{ width: 8, height: 8, borderRadius: 4, background: PAL.brand }} />Покази</span>
              </div>
            </div>
            <div style={{ background: t.bg1, border: `1px solid ${t.line}`, borderRadius: 12, padding: 16 }}>
              <div style={{ height: 260 }}>
                {(() => {
                  const slice = dailyArr.slice(-90);
                  return (
                    <Line
                      data={{
                        labels: slice.map(d => fmtDateShort(d.date || d.keys?.[0])),
                        datasets: [
                          { label: "Кліки",  data: slice.map(d => d.clicks || 0),       borderColor: PAL.brand2, backgroundColor: hexA(PAL.brand2, 0.14), fill: true, tension: 0.4, pointRadius: 0, borderWidth: 2, yAxisID: "y" },
                          { label: "Покази", data: slice.map(d => d.impressions || 0), borderColor: PAL.brand,  backgroundColor: hexA(PAL.brand,  0.08), fill: true, tension: 0.4, pointRadius: 0, borderWidth: 2, yAxisID: "y1" },
                        ],
                      }}
                      options={chartOpts(t, rpDark, { dualY: true })}
                    />
                  );
                })()}
              </div>
            </div>
            {/* 30 vs 30 compare */}
            {dailyArr.length >= 60 && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12 }}>
                {[
                  { l: "Кліки", now: last30Clicks, prev: prev30Clicks, c: PAL.brand2 },
                  { l: "Покази", now: last30Imp, prev: prev30Imp, c: PAL.brand },
                ].map((m, i) => {
                  const d = m.now - m.prev;
                  const pct = m.prev > 0 ? Math.round((d / m.prev) * 100) : 0;
                  return (
                    <div key={i} style={{ background: t.bg1, border: `1px solid ${t.line}`, borderRadius: 10, padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <div style={{ fontSize: 10, color: t.fg2, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 4 }}>{m.l} · 30д vs попередні 30д</div>
                        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                          <span style={{ fontSize: 22, fontWeight: 600, color: m.c, fontFamily: FONT_MONO }}>{fmtNum(m.prev)}</span>
                          <span style={{ fontSize: 13, color: t.fg3 }}>→</span>
                          <span style={{ fontSize: 22, fontWeight: 600, color: t.fg0, fontFamily: FONT_MONO }}>{fmtNum(m.now)}</span>
                        </div>
                      </div>
                      <ClientChip kind={pct >= 0 ? "pos" : "neg"} t={t}>{pct >= 0 ? "▲" : "▼"} {Math.abs(pct)}%</ClientChip>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {/* ─── POSITIONS DYNAMIC ─────────────────────────────── */}
        {snaps.length > 0 && (
          <section data-blk="positions" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: t.fg0 }}>Динаміка позицій</h2>
                <span style={{ fontSize: 12, color: t.fg2 }}>TOP-1 / TOP-3 / TOP-5 / TOP-10 / TOP-30 / TOP-100 · {snaps.length} знять{latest && ` · останнє ${fmtDate(latest.date || latest.snapshot_date)}`}</span>
              </div>
              {_posVerAvail && (
                <div style={{ display: "inline-flex", background: t.bg2, border: `1px solid ${t.line}`, borderRadius: 10, padding: 3, gap: 2 }}>
                  {[{ id: "main", label: rpData.versionLabel || "Версія 1" }, { id: "v2", label: rpData.versionLabelV2 || "Версія 2" }].map(v => {
                    const active = posVer === v.id;
                    return (
                      <button key={v.id} onClick={() => setPosVer(v.id)}
                        style={{ padding: "6px 16px", fontSize: 13, fontWeight: 700, borderRadius: 8, border: "none", cursor: "pointer", fontFamily: "inherit",
                          background: active ? PAL.brand2 : "transparent", color: active ? "#fff" : t.fg2, transition: "all .15s" }}>
                        {v.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div style={{ background: t.bg1, border: `1px solid ${t.line}`, borderRadius: 12, padding: 16 }}>
              <div style={{ height: 280 }}>
                {recentSnaps.length < 2 ? (
                  <div style={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: t.fg2, gap: 8 }}>
                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>Накопичується історія позицій</div>
                    <div style={{ fontSize: 12 }}>Графік з'явиться після другого знімку</div>
                  </div>
                ) : (
                  <Line
                    data={{
                      labels: recentSnaps.map(s => fmtDateShort(s.date || s.snapshot_date)),
                      datasets: [
                        { label: "TOP-1",   data: recentSnaps.map(s => pctOf(s.top1,   s.totalQueries)), rawCounts: recentSnaps.map(s => s.top1   || 0), borderColor: "#eab308",  backgroundColor: hexA("#eab308", 0.16),  fill: true, tension: 0.4, pointRadius: 3, borderWidth: 2 },
                        { label: "TOP-3",   data: recentSnaps.map(s => pctOf(s.top3,   s.totalQueries)), rawCounts: recentSnaps.map(s => s.top3   || 0), borderColor: PAL.pos,    backgroundColor: hexA(PAL.pos, 0.18),    fill: true, tension: 0.4, pointRadius: 3, borderWidth: 2 },
                        { label: "TOP-5",   data: recentSnaps.map(s => pctOf(s.top5,   s.totalQueries)), rawCounts: recentSnaps.map(s => s.top5   || 0), borderColor: "#14b8a6",  backgroundColor: hexA("#14b8a6", 0.14),  fill: true, tension: 0.4, pointRadius: 3, borderWidth: 2 },
                        { label: "TOP-10",  data: recentSnaps.map(s => pctOf(s.top10,  s.totalQueries)), rawCounts: recentSnaps.map(s => s.top10  || 0), borderColor: PAL.brand2, backgroundColor: hexA(PAL.brand2, 0.12), fill: true, tension: 0.4, pointRadius: 3, borderWidth: 2 },
                        { label: "TOP-30",  data: recentSnaps.map(s => pctOf(s.top30,  s.totalQueries)), rawCounts: recentSnaps.map(s => s.top30  || 0), borderColor: PAL.info,   backgroundColor: hexA(PAL.info, 0.08),   fill: true, tension: 0.4, pointRadius: 3, borderWidth: 2 },
                        { label: "TOP-100", data: recentSnaps.map(s => pctOf(s.top100, s.totalQueries)), rawCounts: recentSnaps.map(s => s.top100 || 0), borderColor: PAL.warn,   backgroundColor: hexA(PAL.warn, 0.06),   fill: true, tension: 0.4, pointRadius: 3, borderWidth: 2 },
                        // ➕ Маркер розширення семантики: плюсик під графіком там, де додали ключі (тултип показує скільки)
                        { label: "➕ Ключі додано", showLine: false, order: 99, pointStyle: "cross", borderColor: PAL.pos, backgroundColor: PAL.pos, pointBorderColor: PAL.pos, pointBackgroundColor: PAL.pos, pointBorderWidth: 3, pointHoverRadius: 10,
                          data:        recentSnaps.map((s, i) => { const pv = i > 0 ? (recentSnaps[i-1].totalQueries || 0) : null; return (pv != null && (s.totalQueries || 0) > pv) ? 0 : null; }),
                          pointRadius: recentSnaps.map((s, i) => { const pv = i > 0 ? (recentSnaps[i-1].totalQueries || 0) : null; return (pv != null && (s.totalQueries || 0) > pv) ? 7 : 0; }),
                          addedCounts: recentSnaps.map((s, i) => { const pv = i > 0 ? (recentSnaps[i-1].totalQueries || 0) : null; return (pv != null && (s.totalQueries || 0) > pv) ? ((s.totalQueries || 0) - pv) : null; }),
                          totPair:     recentSnaps.map((s, i) => { const pv = i > 0 ? (recentSnaps[i-1].totalQueries || 0) : null; return pv != null ? [pv, (s.totalQueries || 0)] : null; }) },
                      ],
                    }}
                    options={chartOpts(t, rpDark, { yPct: true, labelFmt: (ctx) => {
                      if (ctx.dataset.addedCounts) { const n = ctx.dataset.addedCounts[ctx.dataIndex]; if (n == null) return undefined; const p = ctx.dataset.totPair?.[ctx.dataIndex]; return `➕ Додано ${n} ключів${p ? ` (було ${p[0]} → стало ${p[1]})` : ""}`; }
                      return `${ctx.dataset.label}: ${ctx.parsed.y}% (${ctx.dataset.rawCounts?.[ctx.dataIndex] ?? 0} ключів)`;
                    } })}
                  />
                )}
                <div style={{ fontSize: 10, color: t.fg3, marginTop: 6, display: "flex", alignItems: "center", gap: 5 }}>
                  <span style={{ color: PAL.pos, fontWeight: 800, fontSize: 13, lineHeight: 1 }}>+</span>
                  <span>розширення семантики — наведіть на плюсик, щоб побачити, скільки ключів додано</span>
                </div>
              </div>
              <div style={{ display: "flex", gap: 18, marginTop: 12, padding: "10px 0 0", borderTop: `1px solid ${t.line}`, flexWrap: "wrap" }}>
                {[
                  { l: "TOP-1", k: "top1", c: "#eab308" },
                  { l: "TOP-3", k: "top3", c: PAL.pos },
                  { l: "TOP-5", k: "top5", c: "#14b8a6" },
                  { l: "TOP-10", k: "top10", c: PAL.brand2 },
                  { l: "TOP-30", k: "top30", c: PAL.info },
                  { l: "TOP-100", k: "top100", c: t.fg2 },
                ].map((s, i) => {
                  const nowPct = snapPct(sLast, s.k);
                  const lastD = sPrev ? nowPct - snapPct(sPrev, s.k) : null;
                  const wholeD = sFirst ? nowPct - snapPct(sFirst, s.k) : null;
                  const cnt = sLast?.[s.k] || 0, tot = sLast?.totalQueries || 0;
                  const ppStr = (v) => v == null ? "—" : (v >= 0 ? `+${v}` : `${v}`) + " п.п.";
                  const ppCol = (v) => v == null ? t.fg3 : (v > 0 ? PAL.pos : v < 0 ? PAL.neg : t.fg2);
                  return (
                    <div key={i} style={{ flex: 1, minWidth: 140, display: "flex", flexDirection: "column", gap: 3 }}>
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <span style={{ width: 8, height: 8, borderRadius: 4, background: s.c }} />
                        <span style={{ fontSize: 10, fontWeight: 700, color: t.fg2, textTransform: "uppercase", letterSpacing: "0.12em" }}>{s.l}</span>
                      </div>
                      <span style={{ fontSize: 22, fontWeight: 500, color: t.fg0, fontFamily: FONT_MONO }}>{nowPct}%</span>
                      <span style={{ fontSize: 10, color: t.fg3, fontFamily: FONT_MONO }}>{cnt} з {tot} ключів</span>
                      {cmpMode === "compare" && _cmpActive && oldest && latest && (
                        <span style={{ fontSize: 10, color: PAL.brand2, fontFamily: FONT_MONO }}>{fmtDate(cmpFrom)}: {snapPct(oldest, s.k)}% → {fmtDate(cmpTo)}: {snapPct(latest, s.k)}%</span>
                      )}
                      <div style={{ display: "flex", flexDirection: "column", gap: 1, marginTop: 4 }}>
                        <span style={{ fontSize: 10, color: t.fg2 }}>Ост. період: <span style={{ color: ppCol(lastD), fontWeight: 700, fontFamily: FONT_MONO }}>{ppStr(lastD)}</span></span>
                        <span style={{ fontSize: 10, color: t.fg2 }}>Весь період: <span style={{ color: ppCol(wholeD), fontWeight: 700, fontFamily: FONT_MONO }}>{ppStr(wholeD)}</span></span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* 30d avg vs prev 30d avg */}
            {snaps.length >= 60 && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
                {[
                  { l: "TOP-1",   k: "top1",   c: "#eab308" },
                  { l: "TOP-3",   k: "top3",   c: PAL.pos },
                  { l: "TOP-5",   k: "top5",   c: "#14b8a6" },
                  { l: "TOP-10",  k: "top10",  c: PAL.brand2 },
                  { l: "TOP-30",  k: "top30",  c: PAL.info },
                  { l: "TOP-100", k: "top100", c: t.fg2 },
                ].map((m, i) => {
                  const { now, prev } = posAvg[m.k];
                  const d = now - prev;
                  const pct = prev > 0 ? Math.round((d / prev) * 100) : 0;
                  return (
                    <div key={i} style={{ background: t.bg1, border: `1px solid ${t.line}`, borderRadius: 10, padding: "12px 14px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                      <div>
                        <div style={{ fontSize: 9.5, color: t.fg2, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 4 }}>{m.l} · 30д vs попер.</div>
                        <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                          <span style={{ fontSize: 18, fontWeight: 600, color: m.c, fontFamily: FONT_MONO }}>{prev}</span>
                          <span style={{ fontSize: 11, color: t.fg3 }}>→</span>
                          <span style={{ fontSize: 18, fontWeight: 600, color: t.fg0, fontFamily: FONT_MONO }}>{now}</span>
                        </div>
                      </div>
                      <ClientChip kind={d >= 0 ? "pos" : "neg"} t={t}>{d >= 0 ? "▲" : "▼"} {Math.abs(pct)}%</ClientChip>
                    </div>
                  );
                })}
              </div>
            )}
            {aiFor("positions") && <AINarrator t={t} {...aiFor("positions")} />}
          </section>
        )}

        {/* ─── KEYWORD GROUPS + COMPETITORS ─────────────────── */}
        {(keywordGroups.length > 0 || competitors.length > 0) && (
          <section data-blk="keywordGroups" style={{ display: "grid", gridTemplateColumns: keywordGroups.length > 0 && competitors.length > 0 ? "1.6fr 1fr" : "1fr", gap: 14 }}>
            {keywordGroups.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
                  <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: t.fg0 }}>Запити за групами</h3>
                  <span style={{ fontSize: 11, color: t.fg2 }}>{keywordGroups.length} груп · є хоча б один ключ у TOP-30</span>
                </div>
                <div style={{ background: t.bg1, border: `1px solid ${t.line}`, borderRadius: 12, overflow: "hidden" }}>
                  {keywordGroups.map((g, i) => {
                    const isOpen = expandedGroups.has(g.url);
                    const avg = g.avgPosition;
                    const avgC = avg == null ? t.fg3 : avg <= 3 ? PAL.pos : avg <= 10 ? PAL.brand2 : avg <= 30 ? PAL.info : t.fg3;
                    return (
                      <div key={g.url} style={{ borderTop: i > 0 ? `1px solid ${t.line}` : "none" }}>
                        <button
                          type="button"
                          onClick={() => toggleGroup(g.url)}
                          style={{
                            width: "100%", display: "flex", alignItems: "center", gap: 12,
                            padding: "11px 14px", background: isOpen ? t.bg2 : "transparent",
                            border: "none", textAlign: "left", cursor: "pointer", color: t.fg0,
                          }}
                        >
                          <span style={{ display: "inline-block", width: 12, color: t.fg2, fontFamily: FONT_MONO, fontSize: 11 }}>{isOpen ? "▾" : "▸"}</span>
                          <span style={{ flex: 1, fontSize: 13, fontWeight: 500, color: t.fg0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.head}</span>
                          <span style={{ fontSize: 10.5, color: t.fg3, fontFamily: FONT_MONO, minWidth: 60, textAlign: "right" }}>{g.foundCount}/{g.kwCount}</span>
                          <span style={{ fontSize: 10.5, color: PAL.pos, fontFamily: FONT_MONO, minWidth: 50, textAlign: "right" }}>T3: {g.top3}</span>
                          <span style={{ fontSize: 10.5, color: PAL.brand2, fontFamily: FONT_MONO, minWidth: 60, textAlign: "right" }}>T10: {g.top10}</span>
                          <span style={{ fontSize: 13, fontWeight: 700, color: avgC, fontFamily: FONT_MONO, minWidth: 50, textAlign: "right" }}>{avg != null ? avg.toFixed(1) : "—"}</span>
                        </button>
                        {isOpen && (
                          <div style={{ background: t.bg0, padding: "0 0 6px 0" }}>
                            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                              <thead>
                                <tr style={{ color: t.fg3 }}>
                                  <th style={{ padding: "6px 14px 6px 38px", textAlign: "left", fontSize: 10, letterSpacing: "0.1em", fontWeight: 600 }}>ЗАПИТ</th>
                                  <th style={{ padding: "6px 8px", textAlign: "right", fontSize: 10, letterSpacing: "0.1em", fontWeight: 600 }}>ЧАСТОТА</th>
                                  <th style={{ padding: "6px 14px", textAlign: "right", fontSize: 10, letterSpacing: "0.1em", fontWeight: 600 }}>ПОЗ.</th>
                                </tr>
                              </thead>
                              <tbody>
                                {g.keywords.map((k, j) => {
                                  const p = k.position;
                                  const pc = p == null ? t.fg3 : p <= 3 ? PAL.pos : p <= 10 ? PAL.brand2 : p <= 30 ? PAL.info : t.fg3;
                                  return (
                                    <tr key={j}>
                                      <td style={{ padding: "5px 14px 5px 38px", color: t.fg1, maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{k.keyword}</td>
                                      <td style={{ padding: "5px 8px", textAlign: "right", color: t.fg3, fontFamily: FONT_MONO }}>{k.frequency ? fmtNum(k.frequency) : "—"}</td>
                                      <td style={{ padding: "5px 14px", textAlign: "right", fontFamily: FONT_MONO, color: pc, fontWeight: 600 }}>{p != null ? p : "—"}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {competitors.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: t.fg0 }}>Конкуренти у видачі</h3>
                <div style={{ background: t.bg1, border: `1px solid ${t.line}`, borderRadius: 12, padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
                  {competitors.slice(0, 8).map((c, i) => {
                    const name = typeof c === "string" ? c : (c.domain || c.name || c.url || "—");
                    const dr = typeof c === "object" ? (c.dr || c.domain_rank) : null;
                    const compTop10 = typeof c === "object" ? (c.top10 || c.top_10) : null;
                    const meTop10 = top10Now;
                    const diff = compTop10 != null ? meTop10 - compTop10 : null;
                    const drC = dr != null ? (dr > 70 ? PAL.neg : dr > 45 ? PAL.warn : PAL.info) : t.fg3;
                    return (
                      <div key={i} style={{ padding: "10px 14px", background: t.bg2, borderRadius: 8, border: `1px solid ${t.line}` }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: t.fg0, fontFamily: FONT_MONO, marginBottom: 4 }}>{name}</div>
                        {(dr != null || compTop10 != null) && (
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10.5 }}>
                            {dr != null && <span style={{ color: drC }}>DR <strong>{dr}</strong></span>}
                            {compTop10 != null && <span style={{ color: t.fg2, fontFamily: FONT_MONO }}>TOP-10: {compTop10}</span>}
                          </div>
                        )}
                        {dr != null && (
                          <div style={{ height: 4, background: t.bg3, borderRadius: 2, marginTop: 8, overflow: "hidden" }}>
                            <div style={{ width: `${Math.min(100, dr)}%`, height: "100%", background: drC }} />
                          </div>
                        )}
                        {diff != null && (
                          <div style={{ fontSize: 10, color: diff >= 0 ? PAL.pos : t.fg3, marginTop: 6, fontFamily: FONT_MONO, fontWeight: 600 }}>
                            {diff >= 0 ? `▲ +${diff} попереду` : `▼ ${diff} відстаємо`}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </section>
        )}

        {/* ─── ВЧ (ВИСОКОЧАСТОТНІ) КЛЮЧІ ─────────────────────── */}
        {hfKeywords.length > 0 && (
          <section data-blk="hfKeywords" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: t.fg0 }}>
                  <span style={{ display: "inline-block", fontSize: 11, fontWeight: 800, color: "#fff", background: PAL.brand2, borderRadius: 6, padding: "2px 8px", marginRight: 8, verticalAlign: "middle", letterSpacing: "0.08em" }}>ВЧ</span>
                  Високочастотні ключі
                </h2>
                <span style={{ fontSize: 12, color: t.fg2 }}>{(_useV2 ? rpData.hfManualV2 : rpData.hfManual) ? "Ручний список ВЧ" : `Частотність ≥ ${hfMinFreq.toLocaleString("uk-UA")}`} · {hfKeywords.length} ключів{hfLast ? ` · останнє ${fmtDate(hfLast.date)}` : ""}</span>
              </div>
              {_posVerAvail && (
                <div style={{ display: "inline-flex", background: t.bg2, border: `1px solid ${t.line}`, borderRadius: 10, padding: 3, gap: 2 }}>
                  {[{ id: "main", label: rpData.versionLabel || "Версія 1" }, { id: "v2", label: rpData.versionLabelV2 || "Версія 2" }].map(v => {
                    const active = posVer === v.id;
                    return (
                      <button key={v.id} onClick={() => setPosVer(v.id)}
                        style={{ padding: "6px 16px", fontSize: 13, fontWeight: 700, borderRadius: 8, border: "none", cursor: "pointer", fontFamily: "inherit",
                          background: active ? PAL.brand2 : "transparent", color: active ? "#fff" : t.fg2, transition: "all .15s" }}>
                        {v.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            {hfLast && (
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                {[
                  { l: "ВЧ у ТОП-3", v: hfLast.top3, c: PAL.pos },
                  { l: "ВЧ у ТОП-10", v: hfLast.top10, c: PAL.brand2 },
                  { l: "ВЧ у ТОП-30", v: hfLast.top30, c: PAL.info },
                ].map((s, i) => {
                  const pct = hfLast.hfTotal > 0 ? Math.round((s.v / hfLast.hfTotal) * 100) : 0;
                  return (
                    <div key={i} style={{ flex: 1, minWidth: 150, background: t.bg1, border: `1px solid ${t.line}`, borderRadius: 10, padding: "12px 14px" }}>
                      <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 4 }}>
                        <span style={{ width: 8, height: 8, borderRadius: 4, background: s.c }} />
                        <span style={{ fontSize: 10, fontWeight: 700, color: t.fg2, textTransform: "uppercase", letterSpacing: "0.1em" }}>{s.l}</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                        <span style={{ fontSize: 22, fontWeight: 500, color: t.fg0, fontFamily: FONT_MONO }}>{pct}%</span>
                        <span style={{ fontSize: 11, color: t.fg3, fontFamily: FONT_MONO }}>{s.v} з {hfLast.hfTotal}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {hfSnaps.length >= 2 && (
              <div style={{ background: t.bg1, border: `1px solid ${t.line}`, borderRadius: 12, padding: 16 }}>
                <div style={{ height: 180 }}>
                  <Line
                    data={{
                      labels: hfSnaps.map(s => fmtDateShort(s.date)),
                      datasets: [
                        { label: "TOP-3",  data: hfSnaps.map(s => pctOf(s.top3,  s.hfTotal)), rawCounts: hfSnaps.map(s => s.top3),  borderColor: PAL.pos,    backgroundColor: hexA(PAL.pos, 0.15),    fill: true, tension: 0.4, pointRadius: 2, borderWidth: 2 },
                        { label: "TOP-10", data: hfSnaps.map(s => pctOf(s.top10, s.hfTotal)), rawCounts: hfSnaps.map(s => s.top10), borderColor: PAL.brand2, backgroundColor: hexA(PAL.brand2, 0.10), fill: true, tension: 0.4, pointRadius: 2, borderWidth: 2 },
                        { label: "TOP-30", data: hfSnaps.map(s => pctOf(s.top30, s.hfTotal)), rawCounts: hfSnaps.map(s => s.top30), borderColor: PAL.info,   backgroundColor: hexA(PAL.info, 0.07),   fill: true, tension: 0.4, pointRadius: 2, borderWidth: 2 },
                      ],
                    }}
                    options={chartOpts(t, rpDark, { yPct: true, labelFmt: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y}% (${ctx.dataset.rawCounts?.[ctx.dataIndex] ?? 0} ключів)` })}
                  />
                </div>
              </div>
            )}
            <div style={{ background: t.bg1, border: `1px solid ${t.line}`, borderRadius: 12, overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                <thead>
                  <tr style={{ background: t.bg2 }}>
                    <th style={{ textAlign: "left", padding: "10px 14px", fontSize: 10, fontWeight: 700, color: t.fg2, textTransform: "uppercase", letterSpacing: "0.1em" }}>Ключ</th>
                    <th style={{ textAlign: "right", padding: "10px 14px", fontSize: 10, fontWeight: 700, color: t.fg2, textTransform: "uppercase", letterSpacing: "0.1em" }}>Частотн.</th>
                    <th style={{ textAlign: "center", padding: "10px 14px", fontSize: 10, fontWeight: 700, color: t.fg2, textTransform: "uppercase", letterSpacing: "0.1em" }}>Позиція</th>
                    <th style={{ textAlign: "center", padding: "10px 14px", fontSize: 10, fontWeight: 700, color: t.fg2, textTransform: "uppercase", letterSpacing: "0.1em" }}>Зміна</th>
                    <th style={{ textAlign: "center", padding: "10px 14px", fontSize: 10, fontWeight: 700, color: t.fg2, textTransform: "uppercase", letterSpacing: "0.1em" }}>ТОП</th>
                  </tr>
                </thead>
                <tbody>
                  {hfKeywords.map((k, i) => {
                    const p = k.position;
                    const bucket = p == null ? "—" : p <= 3 ? "TOP-3" : p <= 10 ? "TOP-10" : p <= 30 ? "TOP-30" : p <= 100 ? "TOP-100" : "100+";
                    const bcol = p == null ? t.fg3 : p <= 3 ? PAL.pos : p <= 10 ? PAL.brand2 : p <= 30 ? PAL.info : t.fg2;
                    const d = (p != null && k.prevPosition != null) ? (k.prevPosition - p) : null; // >0 = піднявся
                    return (
                      <tr key={i} style={{ borderTop: `1px solid ${t.line}` }}>
                        <td style={{ padding: "9px 14px", color: t.fg0 }}>{k.keyword}</td>
                        <td style={{ padding: "9px 14px", textAlign: "right", color: t.fg1, fontFamily: FONT_MONO }}>{(k.frequency || 0).toLocaleString("uk-UA")}</td>
                        <td style={{ padding: "9px 14px", textAlign: "center", fontWeight: 700, color: bcol, fontFamily: FONT_MONO }}>{p != null ? p : "—"}</td>
                        <td style={{ padding: "9px 14px", textAlign: "center", fontFamily: FONT_MONO }}>{d == null ? <span style={{ color: t.fg3 }}>—</span> : <span style={{ color: d > 0 ? PAL.pos : d < 0 ? PAL.neg : t.fg2, fontWeight: 700 }}>{d > 0 ? `▲${d}` : d < 0 ? `▼${Math.abs(d)}` : "="}</span>}</td>
                        <td style={{ padding: "9px 14px", textAlign: "center" }}><span style={{ fontSize: 10, fontWeight: 700, color: bcol, border: `1px solid ${bcol}40`, borderRadius: 6, padding: "2px 8px" }}>{bucket}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* ─── INDEXATION + COVERAGE ─────────────────────────── */}
        {coverageTotal > 0 && (
          <section data-blk="coverage" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: t.fg0 }}>Індексація</h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
              {[
                { l: "В індексі", v: indexed, c: PAL.pos, h: "Сторінок видно в Google" },
                { l: "Не в індексі", v: coverageTotal - indexed, c: PAL.warn, h: "Не індексуються (дублі/closed)" },
                { l: "Загалом", v: coverageTotal, c: t.fg1, h: "Всі відомі сторінки" },
                { l: "% покриття", v: Math.round((indexed / Math.max(1, coverageTotal)) * 100), c: PAL.brand2, h: "Частка індексованих", suffix: "%" },
              ].map((m, i) => (
                <div key={i} style={{ background: t.bg1, border: `1px solid ${t.line}`, borderRadius: 12, padding: 14, display: "flex", flexDirection: "column", gap: 6, position: "relative", overflow: "hidden" }}>
                  <div style={{ position: "absolute", top: 0, left: 0, width: "100%", height: 2, background: m.c, opacity: 0.6 }} />
                  <span style={{ fontSize: 10, fontWeight: 700, color: t.fg2, textTransform: "uppercase", letterSpacing: "0.12em" }}>{m.l}</span>
                  <div style={{ fontSize: 26, fontWeight: 500, color: m.c, letterSpacing: "-0.02em", fontFamily: FONT_MONO }}>{fmtNum(m.v)}{m.suffix || ""}</div>
                  <div style={{ fontSize: 10.5, color: t.fg3, lineHeight: 1.35 }}>{m.h}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ─── AI-ВИДИМІСТЬ (ChatGPT / Perplexity / Gemini) ──── */}
        {aiVis && Array.isArray(aiVis.results) && aiVis.results.length > 0 && (() => {
          const total = aiVis.results.length;
          const citedN = aiVis.results.filter(r => r.cited).length;
          const pct = total ? Math.round((citedN / total) * 100) : 0;
          return (
            <section data-blk="aiVis" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
                <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: t.fg0 }}>Видимість в AI-пошуку</h2>
                <span style={{ fontSize: 12, color: t.fg2 }}>ChatGPT · Perplexity · Gemini — чи згадують ваш сайт у відповідях</span>
              </div>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                <div style={{ background: t.bg1, border: `1px solid ${t.line}`, borderRadius: 12, padding: "16px 20px", minWidth: 160 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: t.fg2, textTransform: "uppercase", letterSpacing: "0.12em" }}>Цитованість</div>
                  <div style={{ fontSize: 26, fontWeight: 500, color: pct >= 50 ? PAL.pos : pct > 0 ? PAL.warn : PAL.neg, fontFamily: FONT_MONO }}>{pct}%</div>
                  <div style={{ fontSize: 10.5, color: t.fg3 }}>{citedN} з {total} запитів</div>
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {[...aiVis.results].sort((a, b) => (b.cited ? 1 : 0) - (a.cited ? 1 : 0) || (a.position ?? 999) - (b.position ?? 999)).slice(0, 30).map((r, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, padding: "8px 12px", background: t.bg1, border: `1px solid ${t.line}`, borderRadius: 8 }}>
                    <span style={{ flex: 1, color: t.fg1 }}>{r.keyword}</span>
                    {r.position != null && <span style={{ fontSize: 11, color: t.fg2, fontFamily: FONT_MONO }}>поз. {r.position}</span>}
                    <span style={{ fontSize: 10.5, fontWeight: 700, padding: "2px 8px", borderRadius: 5, background: r.cited ? hexA(PAL.pos, .15) : hexA(PAL.neg, .12), color: r.cited ? PAL.pos : PAL.neg }}>{r.cited ? "згадується" : "не згадується"}</span>
                  </div>
                ))}
              </div>
            </section>
          );
        })()}

        {/* ─── FOOTER ────────────────────────────────────────── */}
        <section data-blk="_footer" style={{ marginTop: 20, padding: "28px 0 0", borderTop: `1px solid ${t.line}`, display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 20 }}>
            <div style={{ flex: 1, minWidth: 280 }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: PAL.brand2, letterSpacing: "0.18em" }}>SVITLO AGENCY × SPALAH</span>
              <div style={{ fontSize: 18, fontWeight: 600, color: t.fg0, marginTop: 6 }}>Дякуємо за довіру.</div>
              <div style={{ fontSize: 12.5, color: t.fg2, marginTop: 4, lineHeight: 1.55, maxWidth: 540 }}>
                Звіт згенеровано автоматично через Spalah · {period}
              </div>
            </div>
          </div>
          <div style={{ fontSize: 10, color: t.fg3, fontFamily: FONT_MONO, letterSpacing: "0.08em", textAlign: "center", paddingTop: 12 }}>
            виконано через spalah · seo-platform
          </div>
        </section>

      </div>
    </div>
  );
}
